import {
  type EnvironmentId,
  type MessageAgentModel,
  type MessageId,
  type OrchestrationAgentRequest,
  type OrchestrationAwaitedBackgroundTask,
  type OrchestrationCheckBack,
  type ProviderDriverKind,
  PROVIDER_DISPLAY_NAMES,
  type RoomAgentRequestId,
  type ServerProviderSkill,
  type SideTurnId,
  type ThreadId,
  type ThreadParticipantId,
  type TurnId,
} from "@threadlines/contracts";
import { WorkflowRunCard, type WorkflowRunActions } from "./WorkflowRunCard";
import { TickingText } from "../ui/TickingText";
import {
  createContext,
  Fragment,
  memo,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import {
  LegendList,
  type LegendListRef,
  type OnViewableItemsChangedInfo,
} from "@legendapp/list/react";
import { useQueries } from "@tanstack/react-query";
import {
  deriveTimelineEntries,
  formatSubagentListName,
  isActiveSubagentStatus,
  type McpAuthReconnectAction,
  type ProviderAuthReconnectAction,
  type SubagentProgressItem,
  type ThreadSubagentHistoryEntry,
} from "../../session-logic";
import {
  formatLiveAgentStatusRows,
  formatSubagentReceiptSummary,
  liveAgentTrackerSignature,
  mergeSubagentLists,
  parseLiveAgentTrackerSignature,
  selectTurnAgents,
  summarizeTurnAgents,
  type LiveAgentStatusRoster,
  type TurnAgentSummary,
} from "./agentsPanel.logic";
import {
  createUpwardScrollDetector,
  DEFAULT_SCROLL_END_TOLERANCE_PX,
  isScrollMetricsAtEnd,
} from "../ChatView.logic";
import { AGENT_PAGE_TITLE_MIDDLE_PX, AgentPageRow } from "./AgentPageRow";
import { McpAppCard } from "./McpAppCard";
import {
  CodeBlockRunScopeContext,
  CommandOutputMessage,
  CommandRequestRow,
  isCommandOutputMessage,
  type CodeBlockRunScope,
} from "./CommandRuns";
import {
  type ChatAttachment,
  type ChatMessage,
  type SidebarThreadSummary,
  type TurnDiffSummary,
} from "../../types";
import { chatAttachmentPreviewQueryOptions } from "../../lib/attachmentPreviewQuery";
import { environmentRequiresRpcAssetTransport } from "../../environments/runtime";
import { summarizeTurnDiffStats } from "../../lib/turnDiffTree";
import { isProviderAuthErrorChatMessage } from "../../lib/providerAuthMessages";
import { useWsConnectionStatus } from "../../rpc/wsConnectionState";
import ChatMarkdown from "../ChatMarkdown";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BotIcon,
  CheckIcon,
  ChevronRightIcon,
  CircleAlertIcon,
  Clock3Icon,
  FileTextIcon,
  GitForkIcon,
  KeyRoundIcon,
  LoaderIcon,
  LogInIcon,
  RefreshCwIcon,
  ReplyIcon,
  PencilIcon,
  SplitIcon,
  SquarePenIcon,
  TerminalIcon,
  Undo2Icon,
} from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { buildExpandedImagePreview, ExpandedImagePreview } from "./ExpandedImagePreview";
import type { FilePreviewRequest } from "./FilePreviewDialog";
import { loadChatAttachmentBlob } from "../../lib/attachmentPreviewQuery";
import { ProposedPlanCard, type ProposedPlanCardStatus } from "./ProposedPlanCard";
import { ChangedFilesTree } from "./ChangedFilesTree";
import { DiffStatLabel, hasNonZeroStat } from "./DiffStatLabel";
import { MessageCopyButton } from "./MessageCopyButton";
import { ActivityGroup } from "./ActivityGroup";
import {
  activityStepFromWorkLogEntry,
  newestThoughtSentence,
  threadlinesRoomToolOf,
  type ActivityStep,
} from "./activitySteps";
import { useLiveCommandOutputLine } from "./backgroundRunOutput";
import {
  describeThreadStartCall,
  parseThreadStartResult,
  threadStartCallHasRecord,
  useChildRequestState,
  useChildThreadsStartedIn,
  useThreadTitle,
  type ThreadStartCall,
} from "../../childThreads";
import { resolveThreadStatusPill } from "../Sidebar.logic";
import { ThreadProviderGlyph, ThreadStatusText } from "../sidebar/InboxRows";
import {
  computeStableMessagesTimelineRows,
  NO_STABLE_TIMELINE_ROWS,
  deriveMessagesTimelineRows,
  deriveRememberedMessagesTimelineRows,
  estimateTimelineRowHeight,
  resolveAssistantMessageCopyState,
  shouldCollapseUserMessage,
  stretchDurationMs,
  timelineMessageView,
  timelineRowTurnIds,
  type StableMessagesTimelineRowsState,
  type TurnSummary,
  type MessagesTimelineRow,
} from "./MessagesTimeline.logic";
import {
  isSettledThreadStartEntry,
  type SideAnswerContext,
  threadStartTurnKey,
} from "./timelineRowsShared";
import { TerminalContextInlineChip } from "./TerminalContextInlineChip";
import {
  PickedElementContextChip,
  PickedElementContextGroupChip,
} from "./ComposerPendingPickedElementContexts";
import {
  TRANSCRIPT_HIGHLIGHT_CARD_LABEL_CLASS_NAME,
  TranscriptHighlightContextCard,
} from "./TranscriptHighlightContextCard";
import { TooltipWrapper } from "../ui/tooltip";
import { describeCheckBack, formatCheckBackTime, nextCheckBack } from "./checkBacks";
import { useRelativeTimeTick } from "../../hooks/useRelativeTimeTick";
import {
  deriveDisplayedUserMessageState,
  type ParsedTerminalContextEntry,
} from "~/lib/terminalContext";
import {
  groupPickedElementContexts,
  type PickedElementContext,
  type PickedElementContextDraft,
} from "~/lib/pickedElementContext";
import {
  formatParsedDrawingDescriptor,
  type ParsedDrawingContextEntry,
} from "~/lib/drawingContext";
import { cn, pluralize } from "~/lib/utils";
import { useUiStateStore } from "~/uiStateStore";
import { type TimestampFormat } from "@threadlines/contracts/settings";
import { formatTimestamp } from "../../timestampFormat";
import { useSettings } from "../../hooks/useSettings";
import { useStreamingHeightFloor } from "../../hooks/useStreamingHeightFloor";
import { useStreamingTextReveal } from "../../hooks/useStreamingTextReveal";
import { findSearchTextHighlightSpans } from "../../lib/searchTextHighlight";

import {
  buildInlineTerminalContextText,
  splitUserMessageText,
} from "./userMessageTerminalContexts";
import { SkillInlineText } from "./SkillInlineText";
import {
  finiteScrollMetric,
  revealTimelineRow,
  scrollRectIntoTimelineView,
} from "./timelineReveal";
import {
  WorkingAnchorDots,
  isWaitingOnUserState,
  workingDotsStateForLabel,
} from "./WorkingAnchorDots";
import { formatWorkspaceRelativePath } from "../../filePathDisplay";
import { formatProviderDriverKindLabel } from "../../providerModels";
import { useLocalImagePreview } from "~/hooks/useLocalImagePreview";
import type {
  ParsedTranscriptHighlightContextEntry,
  TranscriptHighlightContextSelection,
  TranscriptHighlightSourceRole,
} from "~/lib/transcriptHighlightContext";
import { formatTranscriptHighlightContextPreview } from "~/lib/transcriptHighlightContext";
import { ProviderInstanceIcon } from "./ProviderInstanceIcon";
import {
  type AgentModelLabeler,
  describeRoomAgentMessage,
  describeRoomInvite,
  messageAgentLabels,
  type OwnAgentLabels,
  type RoomAgentLabel,
  type RoomAgentNames,
  roomAgentKey,
  sameRoomAgentLabels,
  useRoomAgentNames,
} from "../../rooms";
import { useKept, useStableHandlers } from "../../hooks/useStableIdentity";
import { shallow } from "zustand/vanilla/shallow";
import { RoomReviewTag } from "./RoomReviewTag";
import { foldedAnsweringSideTurnIds, type SideAnswerView } from "./sideAnswers";
import { ChatFind } from "./chatFind/ChatFind";
import { TranscriptSelectionLayer } from "./TranscriptSelectionPopover";
import type { ChatFindRowContext } from "./chatFind/chatFindRows";
import { useChatFindStore } from "../../chatFindStore";
import { detailEntranceRef, rowEntranceRef } from "./timelineEntrance";
import { providerInstanceShowsBadge } from "../../providerInstances";

// ---------------------------------------------------------------------------
// Context — shared state consumed by every row component via Context.
// Propagates through LegendList's memo boundaries for shared callbacks and
// non-row-scoped state. `nowIso` is intentionally excluded — self-ticking
// components (WorkingTimer, LiveElapsed) handle it.
// ---------------------------------------------------------------------------

interface TimelineRowSharedState {
  timestampFormat: TimestampFormat;
  /** Room agents by `roomAgentKey`; null outside rooms. */
  roomAgents: ReadonlyMap<string, RoomAgentLabel> | null;
  /** Their names alone, unchanged until one changes: what steps are worded from. */
  roomAgentNames: RoomAgentNames | null;
  /** Names an agent the way a message recorded it; null when unavailable. */
  agentModelLabeler: AgentModelLabeler | null;
  /** Agent messages that start a new speaker's stretch and carry an author line. */
  authorLineMessageIds: ReadonlySet<MessageId>;
  /** A side answer's review or question, for the name its steps carry. */
  sideAnswerContext: ReadonlyMap<SideTurnId, SideAnswerContext>;
  /** Room requests still open, by their request message. */
  openAgentRequestByMessageId: ReadonlyMap<MessageId, OrchestrationAgentRequest>;
  /**
   * Request messages of requests answered on the side (invites, and asks and
   * reviews), by request: a reply to one reads as one line.
   */
  sideAnswerRequests: ReadonlyMap<RoomAgentRequestId, ChatMessage>;
  routeThreadKey: string;
  markdownCwd: string | undefined;
  resolvedTheme: "light" | "dark";
  workspaceRoot: string | undefined;
  skills: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  activeThreadEnvironmentId: EnvironmentId;
  activeThreadId: ThreadId | null;
  turnDiffSummaryByTurnId: ReadonlyMap<TurnId, TurnDiffSummary>;
  providerAuthReconnect: ProviderAuthReconnectAction | null;
  resolvedProviderAuthReconnectIds: ReadonlySet<string>;
  mcpAuthReconnectStatusByServerName: ReadonlyMap<string, McpAuthReconnectStatus>;
  failedTurnRetry: FailedTurnRetryAction | null;
  onRevertUserMessage: (messageId: MessageId) => void;
  onContinueInNewThread?: (messageId: MessageId) => void;
  /** Stops a side answer; absent where side answers cannot be asked. */
  onStopSideAnswer?: ((sideTurnId: SideTurnId) => void) | undefined;
  /** Cancels an agent's check-backs (ChatView.cancelCheckBack). */
  onCancelCheckBack?: ((checkBack: OrchestrationCheckBack) => void) | undefined;
  /** Puts a message an MCP app asked to send into the composer. */
  onPutMcpAppMessage?: ((text: string) => void) | undefined;
  /** Opens or closes a side answer's one-line status to watch it written. */
  onToggleSideAnswer: (sideTurnId: SideTurnId) => void;
  /** Scrolls to a message: a side answer posted below, or its question. */
  onRevealMessage: (messageId: MessageId) => void;
  /** Shows a sent message's picked element again in the preview; absent
   *  outside the desktop app. */
  onRevealPickedElement?: ((context: PickedElementContextDraft) => void) | undefined;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  onPreviewFile: (request: FilePreviewRequest) => void;
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
  onRunProviderAuthReconnect?: (action: ProviderAuthReconnectAction) => void;
  onRunMcpAuthReconnect?: (action: McpAuthReconnectAction) => void;
  searchTargetMessageId: MessageId | null;
  searchTargetQuery: string;
  /** Times find in chat asked each folded row to open, to show a match. */
  findOpenRequests: ReadonlyMap<string, number>;
  activeSearchTargetMessageId: MessageId | null;
  proposedPlanState: TimelineProposedPlanState | null;
  onOpenAgentsPanel: ((agentThreadId: string | null) => void) | null;
  /** See MessagesTimelineProps.workflowRunActions. */
  workflowRunActions: WorkflowRunActions | null;
  /** See MessagesTimelineProps.onOpenThread. */
  onOpenThread: ((threadId: ThreadId) => void) | null;
  /** Every finished `thread_start` call, by turn: a call's record reads its turn's. */
  threadStartCallsByTurn: ReadonlyMap<string, ReadonlyArray<ThreadStartCall>>;
  /** True while the working anchor at the tail is mounted. The per-agent live
   *  status rows render there and only there; a receipt keeps its compact
   *  tracker chip but must not repeat those rows above the exchange. */
  anchorOwnsLiveAgents: boolean;
}

/** The turn's spawned agents, summarized on the turn's activity row. */
export interface TimelineTurnAgentsState {
  /** Live state for the turn in flight; empty for every settled turn. */
  readonly subagents: ReadonlyArray<SubagentProgressItem>;
  /** The thread's durable agent history — the same records the agents panel
   *  lists — so a settled turn's tracker survives the turn ending and a reload. */
  readonly history?: ReadonlyArray<ThreadSubagentHistoryEntry> | undefined;
}

/** Lifecycle context for proposed-plan rows: which plan is still actionable,
 *  and the implement/navigate handlers the active card should expose. */
export interface TimelineProposedPlanState {
  readonly activePlanId: string | null;
  readonly activeThreadId: ThreadId | null;
  readonly onImplement?: (() => void) | undefined;
  readonly onImplementInNewThread?: (() => void) | undefined;
  readonly onDismiss?: (() => void) | undefined;
  readonly onOpenThread: (threadId: ThreadId) => void;
}

interface FailedTurnRetryAction {
  readonly messageId: MessageId;
  readonly isRetrying: boolean;
  readonly onRetry: () => void;
}

interface TimelineRowActivityState {
  isWorking: boolean;
  isRevertingCheckpoint: boolean;
}

const TimelineRowCtx = createContext<TimelineRowSharedState>(null!);
const TimelineRowActivityCtx = createContext<TimelineRowActivityState>(null!);
/** The turn's spawned agents. A context of its own: they report progress
 *  often, and only the rows that show agents read them. */
const TimelineTurnAgentsCtx = createContext<TimelineTurnAgentsState | null>(null);

/**
 * The name an author line gives an agent: `agent` is a room agent, or null for
 * the thread's own; `turnId` is the turn the stretch belongs to, null for the
 * live one; `asWritten` is how the agent was when it wrote there (AgentLine),
 * which wins when known. Its own context, since labels follow every model the
 * thread's agent is sent with, and only author lines need to hear about it.
 */
type AgentLabelResolver = (
  agent: ThreadParticipantId | null,
  turnId: TurnId | null,
  asWritten: MessageAgentModel | null,
) => RoomAgentLabel | undefined;
const TimelineAgentLabelCtx = createContext<AgentLabelResolver>(() => undefined);
const TIMELINE_LIST_HEADER = <div className="h-3 sm:h-4" />;
const TIMELINE_LIST_FOOTER = <div className="h-3 sm:h-4" />;
const EMPTY_TIMELINE_SKILLS: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">> = [];
type McpAuthReconnectStatus = "running" | "completed";
// What holds the list: a finger (until its glide settles) or a mouse button.
// A device with both can hold with each at once, and each lets go on its own.
type ListHolder = "touch" | "pointer";
const EMPTY_MCP_AUTH_RECONNECT_STATUS: ReadonlyMap<string, McpAuthReconnectStatus> = new Map();
const INITIAL_STICK_TO_BOTTOM_FRAME_COUNT = 3;
// The on-screen keyboard resizes the layout over an animation, so the observer
// can fire while the container is still growing. Keep re-sticking a little
// longer than the mount/request paths do.
const VIEWPORT_RESIZE_STICK_TO_BOTTOM_FRAME_COUNT = 6;
const THREAD_SEARCH_TARGET_HIGHLIGHT_MS = 2_200;
const THREAD_SEARCH_TARGET_HIGHLIGHT_NAME = "threadlines-thread-search-match";
const THREAD_SEARCH_TARGET_MAX_TEXT_RANGES = 128;
const THREAD_SEARCH_TARGET_SCROLL_ATTEMPTS = 4;
const THREAD_SEARCH_TARGET_RENDER_ATTEMPTS = 60;
const THREAD_SEARCH_TARGET_VIEWPORT_MARGIN_PX = 24;
const THREAD_SEARCH_TARGET_VIEWPORT_POSITION = 0.28;
const USER_SCROLL_STICK_LOCK_MS = 450;
const TIMELINE_MAINTAIN_END_THRESHOLD_RATIO = 0.01;
const SCROLLBAR_POINTER_GUTTER_PX = 18;
const TOUCH_SCROLL_INTENT_THRESHOLD_PX = 4;
// After a finger lifts, the browser can keep the list moving: momentum, or the
// bounce at either end. The gesture owns the scroll until that motion has been
// quiet this long, and never longer than the cap after the finger lifted.
const TOUCH_SCROLL_SETTLE_MS = 150;
const TOUCH_SCROLL_SETTLE_MAX_MS = 3_000;
const MAINTAIN_SCROLL_AT_END = { animated: false } as const;
// While the reader is above the tail, the working row never anchors: holding it
// still would push the text they are reading up as the response grows. After a
// short scroll LegendList can still count the row as on screen, because it only
// re-measures what is in view once the scroll crosses a row boundary.
const HOLD_READING_POSITION = {
  data: true,
  size: true,
  shouldRestorePosition: (row: MessagesTimelineRow) => row.kind !== "working",
};
type TimelineScrollEvent = {
  readonly nativeEvent?: {
    readonly contentOffset?: {
      readonly y?: number | null;
    };
    readonly contentSize?: {
      readonly height?: number | null;
    };
    readonly layoutMeasurement?: {
      readonly height?: number | null;
    };
    readonly contentInset?: {
      readonly bottom?: number | null;
    };
  };
};

function getTimelineScrollMetrics(event: TimelineScrollEvent) {
  const nativeEvent = event.nativeEvent;
  const viewportLength = finiteScrollMetric(nativeEvent?.layoutMeasurement?.height);
  const contentLength = finiteScrollMetric(nativeEvent?.contentSize?.height);
  if (viewportLength === null || contentLength === null) {
    return null;
  }

  return {
    scrollOffset: finiteScrollMetric(nativeEvent?.contentOffset?.y) ?? 0,
    viewportLength,
    contentLength,
    contentInsetEnd: finiteScrollMetric(nativeEvent?.contentInset?.bottom) ?? 0,
  };
}

function isTimelineScrollEventAtEnd(event: TimelineScrollEvent): boolean | null {
  const metrics = getTimelineScrollMetrics(event);
  if (metrics === null) {
    return null;
  }
  return isScrollMetricsAtEnd({
    ...metrics,
    tolerancePx: DEFAULT_SCROLL_END_TOLERANCE_PX,
  });
}

/** The list's scroll position, read from its scroll node. */
function readTimelineListMetrics(list: LegendListRef | null) {
  const scrollableNode = list?.getScrollableNode?.();
  if (!scrollableNode || typeof scrollableNode !== "object") {
    return null;
  }

  const metrics = scrollableNode as {
    readonly scrollTop?: number | null;
    readonly scrollHeight?: number | null;
    readonly clientHeight?: number | null;
  };
  const viewportLength = finiteScrollMetric(metrics.clientHeight);
  const contentLength = finiteScrollMetric(metrics.scrollHeight);
  if (viewportLength === null || contentLength === null) {
    return null;
  }

  return {
    scrollOffset: finiteScrollMetric(metrics.scrollTop) ?? 0,
    viewportLength,
    contentLength,
  };
}

function isTimelineListAtEnd(list: LegendListRef | null): boolean {
  const metrics = readTimelineListMetrics(list);
  if (metrics === null) {
    return Boolean(list?.getState?.().isAtEnd);
  }
  return isScrollMetricsAtEnd({
    ...metrics,
    tolerancePx: DEFAULT_SCROLL_END_TOLERANCE_PX,
  });
}

/**
 * Run `stick` on each of the next `frameCount` frames and return a cancel
 * function. LegendList settles item measurements across a couple of frames
 * after content or layout changes, so a single scroll-to-end lands short.
 */
function scheduleStickToBottomFrames(frameCount: number, stick: () => void): () => void {
  const frameIds: number[] = [];
  const scheduleFrame = (remainingFrames: number) => {
    const frameId = window.requestAnimationFrame(() => {
      stick();
      if (remainingFrames > 1) {
        scheduleFrame(remainingFrames - 1);
      }
    });
    frameIds.push(frameId);
  };

  scheduleFrame(frameCount);

  return () => {
    for (const frameId of frameIds) {
      window.cancelAnimationFrame(frameId);
    }
  };
}

interface SearchHighlightRegistry {
  readonly set: (name: string, highlight: object) => void;
  readonly get: (name: string) => object | undefined;
  readonly delete: (name: string) => boolean;
}

interface SearchHighlightConstructor {
  new (...ranges: Range[]): object;
}

function findRenderedTimelineMessageRow(
  container: HTMLElement,
  messageId: MessageId,
): HTMLElement | null {
  return (
    [...container.querySelectorAll<HTMLElement>("[data-message-id]")].find(
      (element) => element.dataset.messageId === messageId,
    ) ?? null
  );
}

function applyTimelineSearchTextHighlight(
  messageRow: HTMLElement,
  query: string,
  onHighlightApplied: () => void,
): (() => void) | null {
  const messageBody = messageRow.querySelector<HTMLElement>(
    "[data-transcript-message-body='true']",
  );
  const css = Reflect.get(globalThis, "CSS") as
    | { readonly highlights?: SearchHighlightRegistry }
    | undefined;
  const registry = css?.highlights;
  const HighlightConstructor = Reflect.get(globalThis, "Highlight") as
    | SearchHighlightConstructor
    | undefined;
  if (!messageBody || query.trim().length === 0) {
    return null;
  }
  if (!registry || !HighlightConstructor) {
    onHighlightApplied();
    return null;
  }

  let activeHighlight: object | null = null;
  let scheduledFrameId: number | null = null;
  const clearActiveHighlight = () => {
    if (activeHighlight && registry.get(THREAD_SEARCH_TARGET_HIGHLIGHT_NAME) === activeHighlight) {
      registry.delete(THREAD_SEARCH_TARGET_HIGHLIGHT_NAME);
    }
    activeHighlight = null;
  };
  const applyHighlight = () => {
    scheduledFrameId = null;
    clearActiveHighlight();

    const ranges: Range[] = [];
    const walker = document.createTreeWalker(messageBody, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement;
        return parent?.closest(
          "button, script, style, [aria-hidden='true'], .thread-search-inline-match",
        )
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_ACCEPT;
      },
    });
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const textNode = node as Text;
      for (const span of findSearchTextHighlightSpans(textNode.data, query)) {
        const range = document.createRange();
        range.setStart(textNode, span.start);
        range.setEnd(textNode, span.end);
        ranges.push(range);
        if (ranges.length >= THREAD_SEARCH_TARGET_MAX_TEXT_RANGES) {
          break;
        }
      }
      if (ranges.length >= THREAD_SEARCH_TARGET_MAX_TEXT_RANGES) {
        break;
      }
    }
    if (ranges.length === 0) {
      onHighlightApplied();
      return;
    }

    activeHighlight = new HighlightConstructor(...ranges);
    registry.set(THREAD_SEARCH_TARGET_HIGHLIGHT_NAME, activeHighlight);
    onHighlightApplied();
  };
  const scheduleHighlight = () => {
    if (scheduledFrameId !== null) {
      return;
    }
    scheduledFrameId = window.requestAnimationFrame(applyHighlight);
  };
  const observer = new MutationObserver(scheduleHighlight);
  observer.observe(messageBody, {
    childList: true,
    characterData: true,
    subtree: true,
  });
  applyHighlight();

  return () => {
    observer.disconnect();
    if (scheduledFrameId !== null) {
      window.cancelAnimationFrame(scheduledFrameId);
      scheduledFrameId = null;
    }
    clearActiveHighlight();
  };
}

function findFirstTimelineSearchMatchRect(messageRow: HTMLElement, query: string): DOMRect | null {
  const messageBody = messageRow.querySelector<HTMLElement>(
    "[data-transcript-message-body='true']",
  );
  if (!messageBody || query.trim().length === 0) {
    return null;
  }

  const walker = document.createTreeWalker(messageBody, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.closest("button, script, style, [aria-hidden='true']")
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const textNode = node as Text;
    const firstSpan = findSearchTextHighlightSpans(textNode.data, query)[0];
    if (!firstSpan) {
      continue;
    }
    const range = document.createRange();
    range.setStart(textNode, firstSpan.start);
    range.setEnd(textNode, firstSpan.end);
    const rects = range.getClientRects();
    for (let index = 0; index < rects.length; index += 1) {
      const rect = rects[index];
      if (rect && rect.width > 0 && rect.height > 0) {
        return rect;
      }
    }
  }
  return null;
}

/** Where a followed link puts its row: a fifth of the way down the list. */
const REVEAL_VIEW_POSITION = 0.2;
/** Tries to reach a followed link's row while the list moves under it. */
const REVEAL_ATTEMPTS = 3;

/** Puts a drawn row at REVEAL_VIEW_POSITION, correcting a scroll that aimed by
 *  guessed row heights. */
function settleRevealedRow(list: LegendListRef | null, row: HTMLElement): void {
  scrollRectIntoTimelineView(list, row.getBoundingClientRect(), {
    viewPosition: REVEAL_VIEW_POSITION,
    topInsetPx: 0,
    bottomInsetPx: 0,
    always: true,
  });
}

function revealTimelineSearchMatch(
  list: LegendListRef | null,
  messageRow: HTMLElement,
  query: string,
): void {
  const matchRect = findFirstTimelineSearchMatchRect(messageRow, query);
  if (!matchRect) {
    return;
  }
  scrollRectIntoTimelineView(list, matchRect, {
    viewPosition: THREAD_SEARCH_TARGET_VIEWPORT_POSITION,
    topInsetPx: THREAD_SEARCH_TARGET_VIEWPORT_MARGIN_PX,
    bottomInsetPx: THREAD_SEARCH_TARGET_VIEWPORT_MARGIN_PX,
  });
}

// ---------------------------------------------------------------------------
// Props (public API)
// ---------------------------------------------------------------------------

const EMPTY_SIDE_ANSWERS: ReadonlyArray<SideAnswerView> = [];
const NO_EXPANDED_SIDE_ANSWERS: ReadonlySet<SideTurnId> = new Set();
const NO_FIND_OPEN_REQUESTS: ReadonlyMap<string, number> = new Map();
/** Keys that scroll the list when it has focus. */
const SCROLL_KEYS: ReadonlySet<string> = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
]);
const EMPTY_AGENT_REQUESTS: ReadonlyArray<OrchestrationAgentRequest> = [];
/** Rows past either edge of the screen whose turns count as in view, so an
 *  older turn's steps are on their way before the reader reaches it. */
const VISIBLE_TURN_MARGIN_ROWS = 8;
/** How long the list rests before its visible turns are reported. Opening a
 *  thread passes its top on the way to the end, and a fast scroll passes turns
 *  the reader never stops on; neither should fetch steps. */
const VISIBLE_TURN_SETTLE_MS = 150;

interface MessagesTimelineProps {
  emptyState?: ReactNode;
  /** The thread's detail has not landed yet: an empty list means "not
   *  loaded", not "nothing said" (PendingThreadDetailState). */
  detailPending?: boolean | undefined;
  /** For a thread on a saved (remote) environment, whether that connection is
   *  up; undefined for this app's own server, whose connection the app tracks. */
  savedEnvironmentConnected?: boolean | undefined;
  isWorking: boolean;
  /** The turn settled but provider background work (a command, an agent)
   *  will wake it again; the anchor stays up as "Waiting" until then. */
  isWaitingOnBackgroundTasks?: boolean | undefined;
  /** What that work is, when the server described it. Keep it referentially
   *  stable: it feeds the rows memo. */
  awaitedTasks?: ReadonlyArray<OrchestrationAwaitedBackgroundTask> | null | undefined;
  /** Times the thread's agents set themselves to come back (Thread.checkBacks). */
  checkBacks?: ReadonlyArray<OrchestrationCheckBack> | undefined;
  onCancelCheckBack?: ((checkBack: OrchestrationCheckBack) => void) | undefined;
  /** Puts a message an MCP app card asked to send into the composer. */
  onPutMcpAppMessage?: ((text: string) => void) | undefined;
  activeStatusLabel?: string | undefined;
  activeTurnInProgress: boolean;
  activeTurnId?: TurnId | null;
  activeTurnStartedAt: string | null;
  listRef: React.RefObject<LegendListRef | null>;
  stickToBottomRequestKey?: number;
  timelineEntries: ReturnType<typeof deriveTimelineEntries>;
  /** Side answers, placed among the working turn's rows; see sideAnswers.ts. */
  sideAnswers?: ReadonlyArray<SideAnswerView>;
  onStopSideAnswer?: (sideTurnId: SideTurnId) => void;
  turnDiffSummaryByAssistantMessageId: Map<MessageId, TurnDiffSummary>;
  routeThreadKey: string;
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
  revertTurnCountByUserMessageId: Map<MessageId, number>;
  /** Room agents by `roomAgentKey`; null or absent outside rooms. */
  roomAgents?: ReadonlyMap<string, RoomAgentLabel> | null;
  /** Names an agent the way a message recorded it (OrchestrationMessage.agentModels).
   *  Absent: every line names agents as they are now. */
  agentModelLabeler?: AgentModelLabeler | null;
  /** Outside a room, what the thread's own agent is called, turn by turn.
   *  Absent: its stretches carry no name. */
  ownAgentLabels?: OwnAgentLabels | null;
  /** The agent holding the thread: whose turn the working row is. */
  workingParticipantId?: ThreadParticipantId | null;
  /** Requests room agents made of each other that are not over yet. */
  openAgentRequests?: ReadonlyArray<OrchestrationAgentRequest>;
  onRevertUserMessage: (messageId: MessageId) => void;
  onContinueInNewThread?: (messageId: MessageId) => void;
  onRevealPickedElement?: ((context: PickedElementContextDraft) => void) | undefined;
  onAddTranscriptHighlightContext?: (selection: TranscriptHighlightContextSelection) => void;
  isRevertingCheckpoint: boolean;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  onPreviewFile: (request: FilePreviewRequest) => void;
  activeThreadEnvironmentId: EnvironmentId;
  /** Server thread id backing this timeline; null for views without one
   *  (drafts, previews) — the subagent transcript fetch disables itself. */
  activeThreadId?: ThreadId | null;
  markdownCwd: string | undefined;
  resolvedTheme: "light" | "dark";
  timestampFormat: TimestampFormat;
  workspaceRoot: string | undefined;
  skills?: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  providerAuthReconnect?: ProviderAuthReconnectAction | null;
  failedTurnRetry?: FailedTurnRetryAction | null;
  onRunProviderAuthReconnect?: (action: ProviderAuthReconnectAction) => void;
  mcpAuthReconnectStatusByServerName?: ReadonlyMap<string, McpAuthReconnectStatus>;
  onRunMcpAuthReconnect?: (action: McpAuthReconnectAction) => void;
  onIsAtEndChange: (isAtEnd: boolean) => void;
  /** The turns on screen and a few rows past either edge, reported as the
   *  reader scrolls; the chat fetches older turns' steps from it. */
  onVisibleTurnIdsChange?: ((turnIds: ReadonlyArray<TurnId>) => void) | undefined;
  searchTarget?:
    | {
        readonly messageId: MessageId;
        readonly query: string;
        readonly requestKey: string;
      }
    | null
    | undefined;
  planScrollTarget?:
    | {
        readonly planId: string;
        readonly requestKey: number;
      }
    | null
    | undefined;
  proposedPlanState?: TimelineProposedPlanState | null | undefined;
  turnAgents?: TimelineTurnAgentsState | null | undefined;
  /**
   * Opens the rail's Agents tab, optionally drilled into one agent. Passed in
   * separately from `turnAgents` on purpose: a finished agent's receipt has to
   * stay clickable after the live progress state for the turn has emptied.
   */
  onOpenAgentsPanel?: ((agentThreadId: string | null) => void) | null | undefined;
  /** What a workflow run's card can do: open it, stop it, save its script. */
  workflowRunActions?: WorkflowRunActions | null | undefined;
  /**
   * Opens another thread in this environment: the one that started this
   * thread, or one this thread's agent started (child threads).
   */
  onOpenThread?: ((threadId: ThreadId) => void) | null | undefined;
}

// ---------------------------------------------------------------------------
// MessagesTimeline — list owner
// ---------------------------------------------------------------------------

export const MessagesTimeline = memo(function MessagesTimeline({
  emptyState,
  detailPending = false,
  savedEnvironmentConnected,
  isWorking,
  isWaitingOnBackgroundTasks = false,
  awaitedTasks = null,
  checkBacks,
  onCancelCheckBack: onCancelCheckBackProp,
  onPutMcpAppMessage: onPutMcpAppMessageProp,
  activeStatusLabel,
  activeTurnInProgress,
  activeTurnId,
  activeTurnStartedAt,
  listRef,
  stickToBottomRequestKey = 0,
  timelineEntries,
  sideAnswers = EMPTY_SIDE_ANSWERS,
  onStopSideAnswer: onStopSideAnswerProp,
  turnDiffSummaryByAssistantMessageId: turnDiffSummaryByAssistantMessageIdProp,
  routeThreadKey,
  onOpenTurnDiff: onOpenTurnDiffProp,
  revertTurnCountByUserMessageId,
  roomAgents: roomAgentsProp = null,
  agentModelLabeler = null,
  ownAgentLabels = null,
  workingParticipantId = null,
  openAgentRequests = EMPTY_AGENT_REQUESTS,
  onRevertUserMessage: onRevertUserMessageProp,
  onContinueInNewThread: onContinueInNewThreadProp,
  onRevealPickedElement: onRevealPickedElementProp,
  onAddTranscriptHighlightContext,
  isRevertingCheckpoint,
  onImageExpand: onImageExpandProp,
  onPreviewFile: onPreviewFileProp,
  activeThreadEnvironmentId,
  activeThreadId = null,
  markdownCwd,
  resolvedTheme,
  timestampFormat,
  workspaceRoot,
  skills = EMPTY_TIMELINE_SKILLS,
  providerAuthReconnect: providerAuthReconnectProp = null,
  failedTurnRetry: failedTurnRetryProp = null,
  onRunProviderAuthReconnect: onRunProviderAuthReconnectProp,
  mcpAuthReconnectStatusByServerName = EMPTY_MCP_AUTH_RECONNECT_STATUS,
  onRunMcpAuthReconnect: onRunMcpAuthReconnectProp,
  onIsAtEndChange,
  onVisibleTurnIdsChange,
  searchTarget = null,
  planScrollTarget = null,
  proposedPlanState: proposedPlanStateProp = null,
  turnAgents = null,
  onOpenAgentsPanel: onOpenAgentsPanelProp = null,
  workflowRunActions: workflowRunActionsProp = null,
  onOpenThread: onOpenThreadProp = null,
}: MessagesTimelineProps) {
  // What every row reads changes only when something rows show changes: the
  // chat rebuilds its callbacks and some of its lookups on every update to
  // the thread, and a new context value would redraw every row on screen.
  const {
    onCancelCheckBack,
    onPutMcpAppMessage,
    onStopSideAnswer,
    onOpenTurnDiff,
    onRevertUserMessage,
    onContinueInNewThread,
    onRevealPickedElement,
    onImageExpand,
    onPreviewFile,
    onRunProviderAuthReconnect,
    onRunMcpAuthReconnect,
    onOpenAgentsPanel,
    onOpenThread,
  } = useStableHandlers({
    onCancelCheckBack: onCancelCheckBackProp,
    onPutMcpAppMessage: onPutMcpAppMessageProp,
    onStopSideAnswer: onStopSideAnswerProp,
    onOpenTurnDiff: onOpenTurnDiffProp,
    onRevertUserMessage: onRevertUserMessageProp,
    onContinueInNewThread: onContinueInNewThreadProp,
    onRevealPickedElement: onRevealPickedElementProp,
    onImageExpand: onImageExpandProp,
    onPreviewFile: onPreviewFileProp,
    onRunProviderAuthReconnect: onRunProviderAuthReconnectProp,
    onRunMcpAuthReconnect: onRunMcpAuthReconnectProp,
    onOpenAgentsPanel: onOpenAgentsPanelProp,
    onOpenThread: onOpenThreadProp,
  });
  const proposedPlanState = useStableHandlers(proposedPlanStateProp);
  const workflowRunActions = useStableHandlers(workflowRunActionsProp);
  const failedTurnRetry = useStableHandlers(failedTurnRetryProp);
  const providerAuthReconnect = useKept(providerAuthReconnectProp, shallow);
  const turnDiffSummaryByAssistantMessageId = useKept(
    turnDiffSummaryByAssistantMessageIdProp,
    shallow,
  );
  const roomAgents = useKept(roomAgentsProp, sameRoomAgentLabels);
  const liveAgentCount = useMemo(
    () =>
      (turnAgents?.subagents ?? []).filter((item) => isActiveSubagentStatus(item.status)).length,
    [turnAgents?.subagents],
  );
  const liveAgentKeysSignature = useMemo(
    () => liveAgentTrackerSignature(turnAgents?.subagents ?? []),
    [turnAgents?.subagents],
  );
  const [expandedSideTurnIds, setExpandedSideTurnIds] =
    useState<ReadonlySet<SideTurnId>>(NO_EXPANDED_SIDE_ANSWERS);
  const onToggleSideAnswer = useCallback((sideTurnId: SideTurnId) => {
    setExpandedSideTurnIds((current) => {
      const next = new Set(current);
      if (!next.delete(sideTurnId)) {
        next.add(sideTurnId);
      }
      return next;
    });
  }, []);
  const roomAgentNames = useRoomAgentNames(roomAgents);
  // In a room with teammates each agent goes by its name there. Anywhere else
  // (a guest brought in for one review included), the thread's own agent goes
  // by the model each of its turns ran on.
  const hasTeammates = useMemo(
    () =>
      roomAgents !== null &&
      [...roomAgents].some(([key, label]) => key !== roomAgentKey(null) && !label.guest),
    [roomAgents],
  );
  const resolveAgentLabel = useCallback<AgentLabelResolver>(
    (agent, turnId, asWritten) =>
      asWritten !== null && agentModelLabeler !== null
        ? agentModelLabeler(agent, asWritten)
        : agent === null && !hasTeammates && ownAgentLabels !== null
          ? turnId === null
            ? ownAgentLabels.latest
            : (ownAgentLabels.byTurn.get(turnId) ?? ownAgentLabels.unrecorded)
          : roomAgents?.get(roomAgentKey(agent)),
    [agentModelLabeler, hasTeammates, ownAgentLabels, roomAgents],
  );
  const openAgentRequestByMessageId = useMemo(
    () => new Map(openAgentRequests.map((request) => [request.requestMessageId, request] as const)),
    [openAgentRequests],
  );
  const rowsInput = useMemo<Parameters<typeof deriveMessagesTimelineRows>[0]>(
    () => ({
      timelineEntries,
      isWorking,
      liveAgentCount,
      liveAgentTrackerKeys: parseLiveAgentTrackerSignature(liveAgentKeysSignature),
      isWaitingOnBackgroundTasks,
      awaitedTasks,
      checkBacks,
      activeStatusLabel,
      activeTurnInProgress,
      activeTurnId: activeTurnId ?? null,
      activeTurnStartedAt,
      turnDiffSummaryByAssistantMessageId,
      revertTurnCountByUserMessageId,
      sideAnswers,
      expandedSideTurnIds,
      openAgentRequestByMessageId,
      workingParticipantId,
      roomAgentNames,
    }),
    [
      workingParticipantId,
      roomAgentNames,
      sideAnswers,
      openAgentRequestByMessageId,
      expandedSideTurnIds,
      timelineEntries,
      isWorking,
      liveAgentCount,
      liveAgentKeysSignature,
      isWaitingOnBackgroundTasks,
      awaitedTasks,
      checkBacks,
      activeStatusLabel,
      activeTurnInProgress,
      activeTurnId,
      activeTurnStartedAt,
      turnDiffSummaryByAssistantMessageId,
      revertTurnCountByUserMessageId,
    ],
  );
  const rawRows = useMemo(
    () => deriveRememberedMessagesTimelineRows(routeThreadKey, rowsInput),
    [routeThreadKey, rowsInput],
  );
  const { result: rows, shared: rowsShared } = useStableRows(rawRows);
  // Kept as stable as the rows, so a live update to one row does not hand
  // every row a new context.
  const {
    anchorOwnsLiveAgents,
    authorLineMessageIds,
    sideAnswerContext,
    sideAnswerRequests,
    threadStartCallsByTurn,
    resolvedProviderAuthReconnectIds,
  } = rowsShared;
  // Find in chat searches the rows as drawn, plus answers still being written
  // under a folded side question: they open where they already sit.
  const findOpen = useChatFindStore((state) => state.open);
  const findRows = useMemo(() => {
    const folded = findOpen ? foldedAnsweringSideTurnIds(sideAnswers, expandedSideTurnIds) : [];
    return folded.length === 0
      ? rows
      : deriveMessagesTimelineRows({
          ...rowsInput,
          expandedSideTurnIds: new Set([...expandedSideTurnIds, ...folded]),
        });
  }, [expandedSideTurnIds, findOpen, rows, rowsInput, sideAnswers]);
  // How many times find asked each folded row to open: each ask opens it,
  // even after the reader folded it again.
  const [findOpenRequests, setFindOpenRequests] =
    useState<ReadonlyMap<string, number>>(NO_FIND_OPEN_REQUESTS);
  const onFindOpenRow = useCallback((rowId: string) => {
    setFindOpenRequests((current) => new Map(current).set(rowId, (current.get(rowId) ?? 0) + 1));
  }, []);
  const onFindOpenSideExchange = useCallback((sideTurnId: SideTurnId) => {
    setExpandedSideTurnIds((current) =>
      current.has(sideTurnId) ? current : new Set([...current, sideTurnId]),
    );
  }, []);
  // Read by the list's viewability callback, which the list keeps across renders.
  const visibleTurnReportRef = useRef({ rows, onVisibleTurnIdsChange });
  useEffect(() => {
    visibleTurnReportRef.current = { rows, onVisibleTurnIdsChange };
  }, [onVisibleTurnIdsChange, rows]);
  const visibleTurnTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (visibleTurnTimerRef.current !== null) {
        window.clearTimeout(visibleTurnTimerRef.current);
      }
    },
    [],
  );
  const handleViewableItemsChanged = useCallback(
    ({ viewableItems }: OnViewableItemsChangedInfo<MessagesTimelineRow>) => {
      if (viewableItems.length === 0) {
        return;
      }
      const indexes = viewableItems.map((item) => item.index);
      const start = Math.min(...indexes) - VISIBLE_TURN_MARGIN_ROWS;
      const end = Math.max(...indexes) + VISIBLE_TURN_MARGIN_ROWS;
      if (visibleTurnTimerRef.current !== null) {
        window.clearTimeout(visibleTurnTimerRef.current);
      }
      visibleTurnTimerRef.current = window.setTimeout(() => {
        visibleTurnTimerRef.current = null;
        const { rows: currentRows, onVisibleTurnIdsChange: report } = visibleTurnReportRef.current;
        report?.(timelineRowTurnIds(currentRows, start, end));
      }, VISIBLE_TURN_SETTLE_MS);
    },
    [],
  );
  const turnDiffSummaryByTurnId = useMemo(() => {
    const next = new Map<TurnId, TurnDiffSummary>();
    for (const summary of turnDiffSummaryByAssistantMessageId.values()) {
      next.set(summary.turnId, summary);
    }
    return next;
  }, [turnDiffSummaryByAssistantMessageId]);
  const searchTargetRowIndex = useMemo(
    () =>
      searchTarget
        ? rows.findIndex(
            (row) => row.kind === "message" && row.message.id === searchTarget.messageId,
          )
        : -1,
    [rows, searchTarget],
  );
  const initialAutoStickToBottom = searchTargetRowIndex < 0;
  const [autoStickToBottom, setAutoStickToBottom] = useState(initialAutoStickToBottom);
  const autoStickToBottomRef = useRef(initialAutoStickToBottom);
  const upwardScrollDetectorRef = useRef(createUpwardScrollDetector());
  const [legendListReady, setLegendListReady] = useState(false);
  const [activeSearchTargetMessageId, setActiveSearchTargetMessageId] = useState<MessageId | null>(
    null,
  );
  const [legendListEpoch, setLegendListEpoch] = useState(0);
  const lastHandledStickToBottomRequestKeyRef = useRef(stickToBottomRequestKey);
  const previousActiveTurnInProgressRef = useRef(activeTurnInProgress);
  const userScrollLockTimerRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  // Set while the reader holds the list: from touchstart until the list stops
  // moving after the finger lifts, or while a mouse button is down on it.
  // Following never scrolls then: a programmatic scroll mid-glide fights the
  // browser's own momentum and edge bounce, which iOS shows as the chat
  // jumping near the bottom, and one mid-drag fights the scroll a selection
  // makes past the list's edge and moves the text under the pointer.
  const listHoldersRef = useRef(new Set<ListHolder>());
  const listHeldRef = useRef(false);
  const [listHeld, setListHeld] = useState(false);
  const touchReleasedAtRef = useRef<number | null>(null);
  const touchSettleTimerRef = useRef<number | null>(null);
  // Whether the hold's latest move left the list at the bottom. Streamed
  // content grows the list without moving it, so this still reads true for a
  // glide that stopped at the bottom while new lines kept arriving below it.
  const holdEndedAtBottomRef = useRef(false);
  // Where the hold last moved the list, and how long the list was then.
  const holdLastMoveRef = useRef<{ scrollOffset: number; contentLength: number } | null>(null);
  const touchLiftListenersRef = useRef<AbortController | null>(null);
  const pointerReleaseListenersRef = useRef<AbortController | null>(null);
  const timelineContainerRef = useRef<HTMLDivElement | null>(null);
  // The timeline's width, for guessing how tall an undrawn row is. Read on
  // first use, then kept current by the resize observer.
  const timelineWidthRef = useRef(0);

  const setAutoStickToBottomState = useCallback((next: boolean) => {
    if (autoStickToBottomRef.current === next) {
      return;
    }
    autoStickToBottomRef.current = next;
    setAutoStickToBottom(next);
  }, []);

  const getEstimatedItemSize = useCallback((row: MessagesTimelineRow) => {
    if (timelineWidthRef.current === 0) {
      timelineWidthRef.current = timelineContainerRef.current?.clientWidth || window.innerWidth;
    }
    return estimateTimelineRowHeight(row, timelineWidthRef.current);
  }, []);

  const assignLegendListRef = useCallback(
    (instance: LegendListRef | null) => {
      listRef.current = instance;
      setLegendListReady(instance !== null);
    },
    [listRef],
  );

  const clearUserScrollLockTimer = useCallback(() => {
    if (userScrollLockTimerRef.current === null) {
      return;
    }
    window.clearTimeout(userScrollLockTimerRef.current);
    userScrollLockTimerRef.current = null;
  }, []);

  useEffect(() => {
    setActiveSearchTargetMessageId(null);
    if (!searchTarget || searchTargetRowIndex < 0) {
      return;
    }

    clearUserScrollLockTimer();
    setAutoStickToBottomState(false);
    onIsAtEndChange(false);
    if (!legendListReady) {
      return;
    }
    setActiveSearchTargetMessageId(searchTarget.messageId);

    let highlightCleanup: (() => void) | null = null;
    let highlightTimerId: number | null = null;
    const stopHighlight = () => {
      highlightCleanup?.();
      highlightCleanup = null;
      setActiveSearchTargetMessageId((current) =>
        current === searchTarget.messageId ? null : current,
      );
    };
    const scheduleStopHighlight = () => {
      if (highlightTimerId === null) {
        highlightTimerId = window.setTimeout(stopHighlight, THREAD_SEARCH_TARGET_HIGHLIGHT_MS);
      }
    };
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const cancelReveal = revealTimelineRow({
      getList: () => listRef.current,
      resolveIndex: () => searchTargetRowIndex,
      findRow: () => {
        const container = timelineContainerRef.current;
        return container ? findRenderedTimelineMessageRow(container, searchTarget.messageId) : null;
      },
      viewPosition: 0.35,
      animated: () => !prefersReducedMotion,
      scrollAttempts: THREAD_SEARCH_TARGET_SCROLL_ATTEMPTS,
      renderAttempts: THREAD_SEARCH_TARGET_RENDER_ATTEMPTS,
      onRow: (messageRow) => {
        highlightCleanup = applyTimelineSearchTextHighlight(messageRow, searchTarget.query, () => {
          revealTimelineSearchMatch(listRef.current, messageRow, searchTarget.query);
        });
        scheduleStopHighlight();
      },
      onGiveUp: scheduleStopHighlight,
    });

    return () => {
      cancelReveal();
      if (highlightTimerId !== null) {
        window.clearTimeout(highlightTimerId);
      }
      highlightCleanup?.();
    };
  }, [
    clearUserScrollLockTimer,
    legendListReady,
    listRef,
    onIsAtEndChange,
    searchTarget,
    searchTargetRowIndex,
    setAutoStickToBottomState,
  ]);

  // A command-palette jump takes the reader somewhere else: an open find bar
  // would describe matches they are no longer looking at.
  useEffect(() => {
    if (searchTarget) useChatFindStore.getState().close();
  }, [searchTarget]);

  // A find jump holds the reader where it put them: a match on the last row
  // leaves the list at its end, and the scroll that gets there must not turn
  // following back on and carry the match away with new output. Held until
  // the bar closes or the reader scrolls (any wheel, scroll key, scrollbar
  // drag or touch), after which following works as it always does.
  const findHoldsPositionRef = useRef(false);
  useEffect(() => {
    if (!findOpen) findHoldsPositionRef.current = false;
  }, [findOpen]);
  const onFindStopFollowing = useCallback(() => {
    findHoldsPositionRef.current = true;
    clearUserScrollLockTimer();
    setAutoStickToBottomState(false);
    onIsAtEndChange(false);
  }, [clearUserScrollLockTimer, onIsAtEndChange, setAutoStickToBottomState]);

  const findRowContext = useMemo<ChatFindRowContext>(
    () => ({
      cwd: markdownCwd,
      skills,
      visualizations: activeThreadId !== null,
      providerAuthReconnect: providerAuthReconnect != null,
      sideAnswerRequests,
    }),
    [activeThreadId, markdownCwd, providerAuthReconnect, sideAnswerRequests, skills],
  );

  const enableAutoStickIfAtEnd = useCallback(() => {
    // A hold owns the list until it ends: a finger that pauses is still
    // mid-gesture, a glide that passes the bottom is still moving, and so is a
    // selection drag. Re-arming then would yank the list under the reader;
    // `settleListHold` re-checks once the hold ends.
    if (
      touchStartYRef.current !== null ||
      listHeldRef.current ||
      !isTimelineListAtEnd(listRef.current)
    ) {
      return;
    }
    setAutoStickToBottomState(true);
    onIsAtEndChange(true);
  }, [listRef, onIsAtEndChange, setAutoStickToBottomState]);

  const scheduleStickReArmCheck = useCallback(() => {
    clearUserScrollLockTimer();
    userScrollLockTimerRef.current = window.setTimeout(() => {
      userScrollLockTimerRef.current = null;
      enableAutoStickIfAtEnd();
    }, USER_SCROLL_STICK_LOCK_MS);
  }, [clearUserScrollLockTimer, enableAutoStickIfAtEnd]);

  const markUserScrollIntent = useCallback(
    (options?: { notifyAwayFromEnd?: boolean }) => {
      setAutoStickToBottomState(false);
      if (options?.notifyAwayFromEnd) {
        onIsAtEndChange(false);
      }
      scheduleStickReArmCheck();
    },
    [onIsAtEndChange, scheduleStickReArmCheck, setAutoStickToBottomState],
  );

  const planScrollTargetRowIndex = useMemo(
    () =>
      planScrollTarget
        ? rows.findIndex(
            (row) =>
              row.kind === "proposed-plan" && row.proposedPlan.id === planScrollTarget.planId,
          )
        : -1,
    [planScrollTarget, rows],
  );

  useEffect(() => {
    if (!planScrollTarget || planScrollTargetRowIndex < 0 || !legendListReady) {
      return;
    }
    markUserScrollIntent({ notifyAwayFromEnd: true });
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    void listRef.current?.scrollToIndex({
      index: planScrollTargetRowIndex,
      animated: !prefersReducedMotion,
      viewPosition: 0.2,
    });
  }, [legendListReady, listRef, markUserScrollIntent, planScrollTarget, planScrollTargetRowIndex]);

  // Read when a link is followed, so following one does not redraw every row
  // whenever the rows change.
  const rowsForRevealRef = useRef(rows);
  useEffect(() => {
    rowsForRevealRef.current = rows;
  }, [rows]);
  const onRevealMessage = useCallback(
    (messageId: MessageId) => {
      // Like the reader's own scroll: the first steps of the scroll can still
      // be near the end, and must not turn following back on under it.
      markUserScrollIntent({ notifyAwayFromEnd: true });
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      // The list aims by guessed heights for rows it has not drawn, and older
      // steps loading on the way can move the row: once there, the row is put
      // where it was meant to go, or found again and aimed at anew.
      revealTimelineRow({
        getList: () => listRef.current,
        resolveIndex: () =>
          rowsForRevealRef.current.findIndex(
            (row) => row.kind === "message" && row.message.id === messageId,
          ),
        findRow: () => {
          const container = timelineContainerRef.current;
          return container ? findRenderedTimelineMessageRow(container, messageId) : null;
        },
        viewPosition: REVEAL_VIEW_POSITION,
        animated: (attemptsLeft) => !prefersReducedMotion && attemptsLeft === REVEAL_ATTEMPTS,
        scrollAttempts: REVEAL_ATTEMPTS,
        renderAttempts: 1,
        onRow: (row) => settleRevealedRow(listRef.current, row),
      });
    },
    [listRef, markUserScrollIntent],
  );

  const stickToBottomNow = useCallback(() => {
    findHoldsPositionRef.current = false;
    clearUserScrollLockTimer();
    setAutoStickToBottomState(true);
    onIsAtEndChange(true);
    void listRef.current?.scrollToEnd?.({ animated: false });
  }, [clearUserScrollLockTimer, listRef, onIsAtEndChange, setAutoStickToBottomState]);

  const clearTouchSettleTimer = useCallback(() => {
    if (touchSettleTimerRef.current === null) {
      return;
    }
    window.clearTimeout(touchSettleTimerRef.current);
    touchSettleTimerRef.current = null;
  }, []);

  const beginListHold = useCallback(
    (holder: ListHolder) => {
      holdEndedAtBottomRef.current = isTimelineListAtEnd(listRef.current);
      holdLastMoveRef.current = readTimelineListMetrics(listRef.current);
      listHoldersRef.current.add(holder);
      if (!listHeldRef.current) {
        listHeldRef.current = true;
        setListHeld(true);
      }
    },
    [listRef],
  );

  const settleListHold = useCallback(
    (holder: ListHolder) => {
      if (holder === "touch") {
        clearTouchSettleTimer();
        touchReleasedAtRef.current = null;
      }
      const holders = listHoldersRef.current;
      if (!holders.delete(holder) || holders.size > 0) {
        return;
      }
      listHeldRef.current = false;
      setListHeld(false);
      // A hold that ends at the bottom follows again, and catches up with
      // whatever streamed in meanwhile. A click is not a scroll, so a find
      // jump keeps holding the reader where it put them.
      if (
        !findHoldsPositionRef.current &&
        (autoStickToBottomRef.current || holdEndedAtBottomRef.current)
      ) {
        stickToBottomNow();
      }
    },
    [clearTouchSettleTimer, stickToBottomNow],
  );

  // Catch-up frames scheduled before a hold began skip the frames that land
  // during it; `settleListHold` catches up once it ends.
  const stickToBottomUnlessHeld = useCallback(() => {
    if (touchStartYRef.current === null && !listHeldRef.current) {
      stickToBottomNow();
    }
  }, [stickToBottomNow]);

  // Called when the finger lifts and on every scroll after it, so the gesture
  // settles once the glide has been quiet for a moment.
  const scheduleTouchScrollSettle = useCallback(() => {
    const releasedAt = touchReleasedAtRef.current;
    if (releasedAt === null) {
      return;
    }
    clearTouchSettleTimer();
    const untilCap = releasedAt + TOUCH_SCROLL_SETTLE_MAX_MS - performance.now();
    touchSettleTimerRef.current = window.setTimeout(
      () => settleListHold("touch"),
      Math.max(0, Math.min(TOUCH_SCROLL_SETTLE_MS, untilCap)),
    );
  }, [clearTouchSettleTimer, settleListHold]);

  const stickToBottomRequestPending =
    stickToBottomRequestKey !== lastHandledStickToBottomRequestKeyRef.current;

  const handleScroll = useCallback(
    (event: TimelineScrollEvent) => {
      scheduleTouchScrollSettle();
      const eventAtEnd = isTimelineScrollEventAtEnd(event);
      const nextIsAtEnd =
        eventAtEnd !== null ? eventAtEnd : Boolean(listRef.current?.getState?.().isAtEnd);
      // Only a report that moved the list speaks for the gesture. The list
      // repeats reports, and sends them up to a frame late, measured after any
      // lines that streamed in meanwhile, so reaching the end of the list as
      // long as it was at the gesture's previous move counts as the bottom.
      const metrics = getTimelineScrollMetrics(event);
      const lastMove = holdLastMoveRef.current;
      if (
        listHeldRef.current &&
        metrics !== null &&
        (lastMove === null || Math.abs(metrics.scrollOffset - lastMove.scrollOffset) >= 1)
      ) {
        holdEndedAtBottomRef.current = isScrollMetricsAtEnd({
          ...metrics,
          contentLength: Math.min(metrics.contentLength, lastMove?.contentLength ?? Infinity),
          tolerancePx: DEFAULT_SCROLL_END_TOLERANCE_PX,
        });
        holdLastMoveRef.current = metrics;
      }
      // A wheel or touch over an agent page scrolls the list with none of its
      // own listeners hearing it; the scroll itself says the reader left.
      if (
        metrics !== null &&
        upwardScrollDetectorRef.current.observe(metrics) &&
        autoStickToBottomRef.current &&
        !stickToBottomRequestPending
      ) {
        markUserScrollIntent({ notifyAwayFromEnd: true });
        return;
      }
      if (!nextIsAtEnd && (autoStickToBottomRef.current || stickToBottomRequestPending)) {
        onIsAtEndChange(true);
        return;
      }
      // Only re-arm from a scroll event once the user-scroll lock has expired.
      // A touch drag clears the intent threshold (a few px) long before it
      // clears the at-end tolerance (24px), so re-arming here would stick the
      // list back to the bottom under the moving finger, report at-end on the
      // next frame, and stick again — the list flickers instead of scrolling.
      // A wheel never hits this because one notch clears the tolerance
      // outright. The lock timer re-checks once scrolling settles, and a hold
      // re-checks once it ends.
      if (
        nextIsAtEnd &&
        userScrollLockTimerRef.current === null &&
        !listHeldRef.current &&
        !findHoldsPositionRef.current
      ) {
        setAutoStickToBottomState(true);
      }
      onIsAtEndChange(nextIsAtEnd);
    },
    [
      listRef,
      markUserScrollIntent,
      onIsAtEndChange,
      scheduleTouchScrollSettle,
      setAutoStickToBottomState,
      stickToBottomRequestPending,
    ],
  );

  const handleWheelCapture = useCallback(
    (event: ReactWheelEvent) => {
      findHoldsPositionRef.current = false;
      if (event.deltaY < 0 && Math.abs(event.deltaY) >= Math.abs(event.deltaX)) {
        markUserScrollIntent({ notifyAwayFromEnd: true });
      }
    },
    [markUserScrollIntent],
  );

  const handlePointerDownCapture = useCallback(
    (event: ReactPointerEvent) => {
      const targetBounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX >= targetBounds.right - SCROLLBAR_POINTER_GUTTER_PX) {
        findHoldsPositionRef.current = false;
        markUserScrollIntent();
      }
      // A mouse button down on the list holds it, as a finger does: a
      // selection dragged past the list's edge scrolls it, often a few pixels
      // at a time, and following would pull it back down under the pointer.
      // Touches hold the list through their own events.
      if (event.pointerType === "touch" || event.button !== 0) {
        return;
      }
      beginListHold("pointer");
      pointerReleaseListenersRef.current?.abort();
      const listeners = new AbortController();
      pointerReleaseListenersRef.current = listeners;
      const { pointerId } = event;
      const release = () => {
        listeners.abort();
        settleListHold("pointer");
      };
      const releaseForPointer = (releaseEvent: PointerEvent) => {
        if (releaseEvent.pointerId === pointerId) {
          release();
        }
      };
      // The button often comes up outside the list. A context menu, or the
      // window losing focus, can swallow the release. The blur listener is
      // not a capturing one: the press itself blurs the composer.
      const options = { capture: true, signal: listeners.signal };
      window.addEventListener("pointerup", releaseForPointer, options);
      window.addEventListener("pointercancel", releaseForPointer, options);
      window.addEventListener("contextmenu", release, options);
      window.addEventListener("blur", release, { signal: listeners.signal });
    },
    [beginListHold, markUserScrollIntent, settleListHold],
  );

  const releaseTouch = useCallback(
    (remainingTouches: number) => {
      if (remainingTouches > 0 || touchStartYRef.current === null) {
        return;
      }
      touchStartYRef.current = null;
      touchReleasedAtRef.current = performance.now();
      scheduleTouchScrollSettle();
      if (!autoStickToBottomRef.current) {
        scheduleStickReArmCheck();
      }
    },
    [scheduleStickReArmCheck, scheduleTouchScrollSettle],
  );

  const handleTouchStartCapture = useCallback(
    (event: ReactTouchEvent) => {
      findHoldsPositionRef.current = false;
      touchStartYRef.current = event.touches[0]?.clientY ?? null;
      touchReleasedAtRef.current = null;
      clearTouchSettleTimer();
      beginListHold("touch");
      // A touch keeps targeting the element it started on. If a streamed
      // update replaces that element mid-gesture, its touchend no longer
      // reaches the list, so also hear the lift on the target itself.
      touchLiftListenersRef.current?.abort();
      const target = event.nativeEvent.target;
      if (target instanceof Node) {
        const listeners = new AbortController();
        touchLiftListenersRef.current = listeners;
        const onLift = (lift: Event) => {
          listeners.abort();
          releaseTouch((lift as TouchEvent).touches.length);
        };
        const options = { passive: true, signal: listeners.signal };
        target.addEventListener("touchend", onLift, options);
        target.addEventListener("touchcancel", onLift, options);
      }
    },
    [beginListHold, clearTouchSettleTimer, releaseTouch],
  );

  const handleTouchMoveCapture = useCallback(
    (event: ReactTouchEvent) => {
      const touchStartY = touchStartYRef.current;
      const currentY = event.touches[0]?.clientY;
      if (touchStartY === null || currentY === undefined) {
        return;
      }
      if (currentY - touchStartY > TOUCH_SCROLL_INTENT_THRESHOLD_PX) {
        markUserScrollIntent({ notifyAwayFromEnd: true });
      }
    },
    [markUserScrollIntent],
  );

  const handleTouchEndCapture = useCallback(
    (event: ReactTouchEvent) => {
      releaseTouch(event.touches.length);
    },
    [releaseTouch],
  );

  const handleKeyDownCapture = useCallback(
    (event: ReactKeyboardEvent) => {
      if (SCROLL_KEYS.has(event.key)) {
        findHoldsPositionRef.current = false;
      }
      if (
        event.key === "ArrowUp" ||
        event.key === "PageUp" ||
        event.key === "Home" ||
        (event.key === " " && event.shiftKey)
      ) {
        markUserScrollIntent({ notifyAwayFromEnd: true });
      }
    },
    [markUserScrollIntent],
  );

  const hasRows = rows.length > 0;

  // LegendList can retain the live tail's old total size when the working row
  // disappears, leaving scrollable space below the settled response. Reset its
  // layout only for a pinned active-to-settled transition; a reader who has
  // scrolled up keeps the current list and position.
  useEffect(() => {
    const wasActiveTurnInProgress = previousActiveTurnInProgressRef.current;
    previousActiveTurnInProgressRef.current = activeTurnInProgress;
    if (
      !wasActiveTurnInProgress ||
      activeTurnInProgress ||
      !hasRows ||
      searchTargetRowIndex >= 0 ||
      findHoldsPositionRef.current
    ) {
      return;
    }
    if (!autoStickToBottomRef.current && !isTimelineListAtEnd(listRef.current)) {
      return;
    }

    clearUserScrollLockTimer();
    setAutoStickToBottomState(true);
    onIsAtEndChange(true);
    setLegendListEpoch((epoch) => epoch + 1);
  }, [
    activeTurnInProgress,
    clearUserScrollLockTimer,
    hasRows,
    listRef,
    onIsAtEndChange,
    searchTargetRowIndex,
    setAutoStickToBottomState,
  ]);

  useEffect(() => {
    if (!legendListReady || !hasRows || searchTargetRowIndex >= 0) {
      return;
    }

    return scheduleStickToBottomFrames(
      INITIAL_STICK_TO_BOTTOM_FRAME_COUNT,
      stickToBottomUnlessHeld,
    );
  }, [
    hasRows,
    legendListEpoch,
    legendListReady,
    routeThreadKey,
    searchTargetRowIndex,
    stickToBottomUnlessHeld,
  ]);

  useEffect(() => {
    if (!hasRows || stickToBottomRequestKey === lastHandledStickToBottomRequestKeyRef.current) {
      return;
    }

    lastHandledStickToBottomRequestKeyRef.current = stickToBottomRequestKey;

    return scheduleStickToBottomFrames(INITIAL_STICK_TO_BOTTOM_FRAME_COUNT, stickToBottomNow);
  }, [hasRows, stickToBottomNow, stickToBottomRequestKey]);

  // A mobile send blurs the composer, and the on-screen keyboard closing
  // resizes the timeline long after the send-time stick frames have run.
  // LegendList restores its anchored offset across that resize, which parks a
  // pinned thread slightly above the message that was just sent. Re-stick on
  // any viewport change while sticking is armed — this also covers rotation,
  // drawer toggles and a growing composer.
  useEffect(() => {
    const container = timelineContainerRef.current;
    if (!container || !legendListReady) {
      return;
    }

    let cancelStickFrames: (() => void) | null = null;
    const restickIfArmed = () => {
      // Mobile browsers collapse and expand the URL bar as the user scrolls,
      // which resizes the visual viewport mid-gesture. Never re-stick while
      // the reader holds the list; `settleListHold` catches up once it ends.
      if (!autoStickToBottomRef.current || touchStartYRef.current !== null || listHeldRef.current) {
        return;
      }
      cancelStickFrames?.();
      cancelStickFrames = scheduleStickToBottomFrames(
        VIEWPORT_RESIZE_STICK_TO_BOTTOM_FRAME_COUNT,
        stickToBottomUnlessHeld,
      );
    };

    // Browsers that resize the layout for the keyboard (Android Chrome, via
    // `interactive-widget=resizes-content`) change the container's box…
    let lastHeight = container.getBoundingClientRect().height;
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            const rect = container.getBoundingClientRect();
            timelineWidthRef.current = rect.width;
            const nextHeight = rect.height;
            if (Math.abs(nextHeight - lastHeight) < 1) {
              return;
            }
            lastHeight = nextHeight;
            restickIfArmed();
          });
    resizeObserver?.observe(container);

    // …while browsers that overlay it (iOS Safari) leave the layout alone and
    // only shrink the visual viewport. Pinch zoom resizes it too, so ignore
    // anything that isn't at a 1:1 scale.
    const viewport = window.visualViewport;
    const handleVisualViewportResize = () => {
      if (viewport && Math.abs(viewport.scale - 1) > 0.01) {
        return;
      }
      restickIfArmed();
    };
    viewport?.addEventListener("resize", handleVisualViewportResize);

    return () => {
      resizeObserver?.disconnect();
      viewport?.removeEventListener("resize", handleVisualViewportResize);
      cancelStickFrames?.();
    };
  }, [legendListReady, stickToBottomUnlessHeld]);

  useEffect(() => {
    // Following never moves the list while the reader holds it (a finger and
    // its glide, or a mouse button): the scroll would fight the gesture.
    // `settleListHold` catches up once the hold ends.
    const shouldFollow = () =>
      stickToBottomRequestPending ||
      (autoStickToBottomRef.current && touchStartYRef.current === null && !listHeldRef.current);
    if (!hasRows || !shouldFollow()) {
      return;
    }

    // Rows above the visual tail can change height both while a turn is live
    // and just after it settles (completion metadata, receipts, late measured
    // markdown). LegendList settles those measurements across a few frames, so
    // keep the pinned tail aligned throughout that window.
    return scheduleStickToBottomFrames(INITIAL_STICK_TO_BOTTOM_FRAME_COUNT, () => {
      if (!shouldFollow()) {
        return;
      }
      void listRef.current?.scrollToEnd?.({ animated: false });
    });
  }, [hasRows, listRef, rows, stickToBottomRequestPending]);

  // While following, pin the bottom in the same frame the list moves its rows.
  // A row that grows measures after layout; the list then moves the rows below
  // it at once but only scrolls to the end on the next frame. When the row
  // that grew sits above the tail (another agent's answer in a room, a step
  // finishing above the reply), the text under it would drop a line for one
  // frame and snap back. The list's position writes are DOM mutations, and
  // their observer runs before that frame paints. The scroll is written
  // directly: the list's scrollToEnd queues a render and lands after paint.
  useEffect(() => {
    const content = timelineContainerRef.current?.querySelector(".legend-list-content-container");
    if (!legendListReady || !content || searchTargetRowIndex >= 0) {
      return;
    }
    const observer = new MutationObserver(() => {
      if (!autoStickToBottomRef.current || touchStartYRef.current !== null || listHeldRef.current) {
        return;
      }
      // Like the list's own follow, only while its last scroll left it at the
      // end; anything else that moved it (a followed link, find in page) wins.
      const state = listRef.current?.getState?.();
      const node = listRef.current?.getScrollableNode?.();
      if (!state?.isWithinMaintainScrollAtEndThreshold || !(node instanceof HTMLElement)) {
        return;
      }
      const end = state.contentLength - node.clientHeight;
      if (end - node.scrollTop > 1) {
        node.scrollTop = end;
      }
    });
    observer.observe(content, { attributes: true, attributeFilter: ["style"], subtree: true });
    return () => observer.disconnect();
  }, [legendListEpoch, legendListReady, listRef, routeThreadKey, searchTargetRowIndex]);

  useEffect(() => {
    return () => {
      clearUserScrollLockTimer();
      clearTouchSettleTimer();
      touchLiftListenersRef.current?.abort();
      pointerReleaseListenersRef.current?.abort();
    };
  }, [clearTouchSettleTimer, clearUserScrollLockTimer]);

  const sharedState = useMemo<TimelineRowSharedState>(
    () => ({
      timestampFormat,
      roomAgents,
      roomAgentNames,
      agentModelLabeler,
      authorLineMessageIds,
      sideAnswerContext,
      openAgentRequestByMessageId,
      sideAnswerRequests,
      routeThreadKey,
      markdownCwd,
      resolvedTheme,
      workspaceRoot,
      skills,
      activeThreadEnvironmentId,
      activeThreadId,
      turnDiffSummaryByTurnId,
      providerAuthReconnect,
      resolvedProviderAuthReconnectIds,
      mcpAuthReconnectStatusByServerName,
      failedTurnRetry,
      onRevertUserMessage,
      ...(onContinueInNewThread ? { onContinueInNewThread } : {}),
      ...(onStopSideAnswer ? { onStopSideAnswer } : {}),
      onToggleSideAnswer,
      onRevealMessage,
      ...(onRevealPickedElement ? { onRevealPickedElement } : {}),
      onImageExpand,
      onPreviewFile,
      onOpenTurnDiff,
      ...(onRunProviderAuthReconnect ? { onRunProviderAuthReconnect } : {}),
      ...(onRunMcpAuthReconnect ? { onRunMcpAuthReconnect } : {}),
      searchTargetMessageId: searchTarget?.messageId ?? null,
      searchTargetQuery: searchTarget?.query ?? "",
      findOpenRequests,
      activeSearchTargetMessageId,
      proposedPlanState,
      onOpenAgentsPanel,
      workflowRunActions,
      onOpenThread,
      onCancelCheckBack,
      onPutMcpAppMessage,
      threadStartCallsByTurn,
      anchorOwnsLiveAgents,
    }),
    [
      timestampFormat,
      roomAgents,
      roomAgentNames,
      agentModelLabeler,
      authorLineMessageIds,
      sideAnswerContext,
      openAgentRequestByMessageId,
      sideAnswerRequests,
      routeThreadKey,
      markdownCwd,
      resolvedTheme,
      workspaceRoot,
      skills,
      activeThreadEnvironmentId,
      activeThreadId,
      turnDiffSummaryByTurnId,
      providerAuthReconnect,
      resolvedProviderAuthReconnectIds,
      mcpAuthReconnectStatusByServerName,
      failedTurnRetry,
      onRevertUserMessage,
      onContinueInNewThread,
      onStopSideAnswer,
      onToggleSideAnswer,
      onRevealMessage,
      onRevealPickedElement,
      onImageExpand,
      onPreviewFile,
      onOpenTurnDiff,
      onRunProviderAuthReconnect,
      onRunMcpAuthReconnect,
      searchTarget?.messageId,
      searchTarget?.query,
      findOpenRequests,
      activeSearchTargetMessageId,
      proposedPlanState,
      onOpenAgentsPanel,
      workflowRunActions,
      onOpenThread,
      onCancelCheckBack,
      onPutMcpAppMessage,
      threadStartCallsByTurn,
      anchorOwnsLiveAgents,
    ],
  );
  const activityState = useMemo<TimelineRowActivityState>(
    () => ({
      isWorking,
      isRevertingCheckpoint,
    }),
    [isRevertingCheckpoint, isWorking],
  );

  // Stable renderItem — no closure deps. Row components read shared state
  // from TimelineRowCtx, which propagates through LegendList's memo.
  const renderItem = useCallback(
    ({ item }: { item: MessagesTimelineRow }) => (
      <div
        className="mx-auto w-full min-w-0 max-w-4xl overflow-x-clip px-2"
        data-timeline-root="true"
      >
        <TimelineRowContent row={item} />
      </div>
    ),
    [],
  );

  if (rows.length === 0 && !isWorking) {
    if (detailPending) {
      return <PendingThreadDetailState savedEnvironmentConnected={savedEnvironmentConnected} />;
    }
    if (emptyState) {
      return (
        // In the new-thread hero the chat column scrolls as one, so this stops
        // filling and scrolling on its own and takes the height of its content.
        <div className="h-full overflow-x-hidden overflow-y-auto overscroll-y-contain draft-hero:h-auto draft-hero:overflow-visible">
          <div className="flex min-h-full flex-col items-center justify-center px-4 py-8 draft-hero:min-h-0 draft-hero:pb-5">
            {emptyState}
          </div>
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-muted-foreground/30">
          Send a message to start the conversation.
        </p>
      </div>
    );
  }

  return (
    <TimelineRowCtx value={sharedState}>
      <TimelineAgentLabelCtx value={resolveAgentLabel}>
        <TimelineRowActivityCtx value={activityState}>
          <TimelineTurnAgentsCtx value={turnAgents}>
            <div className="relative h-full">
              <div ref={timelineContainerRef} className="relative h-full">
                <LegendList<MessagesTimelineRow>
                  key={`${routeThreadKey}:${legendListEpoch}`}
                  ref={assignLegendListRef}
                  data={rows}
                  keyExtractor={keyExtractor}
                  renderItem={renderItem}
                  estimatedItemSize={90}
                  getEstimatedItemSize={getEstimatedItemSize}
                  initialScrollAtEnd={searchTargetRowIndex < 0}
                  {...(searchTargetRowIndex >= 0
                    ? { initialScrollIndex: { index: searchTargetRowIndex, viewPosition: 0.35 } }
                    : {})}
                  maintainScrollAtEnd={
                    searchTargetRowIndex < 0 &&
                    ((autoStickToBottom && !listHeld) || stickToBottomRequestPending)
                      ? MAINTAIN_SCROLL_AT_END
                      : false
                  }
                  maintainScrollAtEndThreshold={TIMELINE_MAINTAIN_END_THRESHOLD_RATIO}
                  // Anchoring and bottom-following both adjust for streamed line wraps.
                  // Use anchoring only while reading above the tail to avoid overshoot.
                  maintainVisibleContentPosition={
                    !autoStickToBottom && !stickToBottomRequestPending
                      ? HOLD_READING_POSITION
                      : false
                  }
                  onScroll={handleScroll}
                  {...(onVisibleTurnIdsChange
                    ? { onViewableItemsChanged: handleViewableItemsChanged }
                    : {})}
                  onWheelCapture={handleWheelCapture}
                  onPointerDownCapture={handlePointerDownCapture}
                  onTouchStartCapture={handleTouchStartCapture}
                  onTouchMoveCapture={handleTouchMoveCapture}
                  onTouchEndCapture={handleTouchEndCapture}
                  onTouchCancelCapture={handleTouchEndCapture}
                  onKeyDownCapture={handleKeyDownCapture}
                  data-chat-messages-list="true"
                  className="h-full overflow-x-hidden overscroll-y-contain px-3 sm:px-5 [scrollbar-gutter:stable_both-edges]"
                  ListHeaderComponent={TIMELINE_LIST_HEADER}
                  ListFooterComponent={TIMELINE_LIST_FOOTER}
                />
                {/* Dissolve rows into the background at the viewport's bottom edge so
              scrolled-under content fades out instead of hard-clipping right
              above the composer. */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-linear-to-t from-background to-transparent"
                />
                {onAddTranscriptHighlightContext ? (
                  <TranscriptSelectionLayer
                    containerRef={timelineContainerRef}
                    onAddHighlight={onAddTranscriptHighlightContext}
                  />
                ) : null}
              </div>
              {/* Beside the list rather than in it: the bar's own keys and its count
              changing are not the transcript's. */}
              <ChatFind
                rows={rows}
                findRows={findRows}
                rowContext={findRowContext}
                listRef={listRef}
                containerRef={timelineContainerRef}
                onOpenRow={onFindOpenRow}
                onOpenSideExchange={onFindOpenSideExchange}
                onStopFollowing={onFindStopFollowing}
              />
            </div>
          </TimelineTurnAgentsCtx>
        </TimelineRowActivityCtx>
      </TimelineAgentLabelCtx>
    </TimelineRowCtx>
  );
});

/**
 * What a chat whose detail has not landed says in place of its messages:
 * loading while its connection is up, and while it is down (the connection
 * notices say why) that it loads once the connection is back, so it never
 * claims to be loading forever. Held back a beat so a fast load never
 * flashes it.
 */
function PendingThreadDetailState(props: { savedEnvironmentConnected: boolean | undefined }) {
  const appConnection = useWsConnectionStatus();
  const connected = props.savedEnvironmentConnected ?? appConnection.phase === "connected";
  return (
    <div className="flex h-full items-center justify-center px-4">
      <p className="loading-reveal text-center text-sm text-muted-foreground" role="status">
        {connected
          ? "Loading conversation…"
          : "This conversation loads when the connection is back."}
      </p>
    </div>
  );
}

function keyExtractor(item: MessagesTimelineRow) {
  return item.id;
}

// ---------------------------------------------------------------------------
// TimelineRowContent — the actual row component
// ---------------------------------------------------------------------------

type TimelineWorkEntry = Extract<MessagesTimelineRow, { kind: "work" }>["groupedEntries"][number];
type TimelineRow = MessagesTimelineRow;
type TimelineImagePreviewItem = {
  id: string;
  name: string;
  previewUrl?: string;
  /** Set instead of `previewUrl` when the provider only named the file; the
   *  grid loads it over the workspace RPC. */
  path?: string;
};

/**
 * Room below a row. A note sits right on top of its steps. The agent's work
 * and a page's panel leave half a gap and the row after it adds the other
 * half. Your messages and plans keep a full gap.
 */
function rowBottomPadding(row: TimelineRow): string {
  switch (row.kind) {
    case "message":
      if (row.message.role !== "assistant") {
        // A question one agent asks another inside its own stretch keeps its
        // answer close under it.
        return row.message.fromAgent !== undefined && row.agentLine?.placement === "through"
          ? "pb-1"
          : "pb-4";
      }
      return row.turnSummary === null ? "pb-1" : "pb-2";
    case "work":
    case "subagent-result":
    case "workflow-run":
    case "mcp-app":
    case "working":
    case "page":
      return "pb-2";
    default:
      return "pb-4";
  }
}

/**
 * Where a row's piece of its agent's line starts and bends, measured from the
 * row's top: the line leaves from under the logo in the row's author line, and
 * bends into the row's first line of text. Each follows the row's own top
 * padding (pt-2 for padTop, then its section's).
 */
function agentLineOffsets(row: TimelineRow): CSSProperties {
  const padTop = row.padTop ? 8 : 0;
  // The section's top padding, then the middle of its first line.
  const [sectionTop, firstLine] =
    row.kind === "working"
      ? [4, 8]
      : row.kind === "side-status"
        ? row.state === "failed" || row.state === "stopped"
          ? [0, 8]
          : [4, 8]
        : row.kind === "message"
          ? [2, 13]
          : row.kind === "subagent-result"
            ? // The tile's border and padding, then its 20px line.
              [0, 15]
            : row.kind === "page"
              ? // The panel's padding, then its title line.
                [0, AGENT_PAGE_TITLE_MIDDLE_PX]
              : [2, 10];
  return {
    // Below the logo: the author line's 16px, less the logo's 1px inset.
    ["--agent-line-top" as string]: `${padTop + sectionTop + 18}px`,
    // The bend's 7px box ends on the first line's middle.
    ["--agent-hook-top" as string]: `${padTop + sectionTop + firstLine - 6}px`,
  };
}

const TimelineRowContent = memo(function TimelineRowContent({ row }: { row: TimelineRow }) {
  const ctx = use(TimelineRowCtx);
  const isActiveSearchTarget =
    row.kind === "message" && row.message.id === ctx.activeSearchTargetMessageId;
  const agentLine = row.agentLine;
  return (
    <div
      // A row whose section renders nothing (e.g. an all-anchor work group
      // with no resolvable tracker) must not leave a phantom padded gap.
      // Every row keeps the lane at the left, where agents' logos and lines
      // are drawn (index.css, [data-agent-line]); a row set in under a side
      // question keeps a second lane for its answering agent's own line.
      className={cn(
        rowBottomPadding(row),
        row.padTop && "pt-2",
        agentLine?.nested ? "pl-10" : "pl-5",
        "[&:not(:has(*))]:p-0",
        isActiveSearchTarget && "thread-search-target-pulse",
      )}
      data-agent-line={agentLine?.placement}
      data-agent-nested-line={agentLine?.nested ?? undefined}
      style={agentLine ? agentLineOffsets(row) : undefined}
      data-timeline-row-id={row.id}
      data-timeline-row-kind={row.kind}
      data-message-id={row.kind === "message" ? row.message.id : undefined}
      data-message-role={row.kind === "message" ? row.message.role : undefined}
      data-thread-search-target={isActiveSearchTarget ? "true" : undefined}
    >
      {row.kind === "work" ? <WorkGroupSection row={row} /> : null}
      {row.kind === "message" ? <MessageTimelineRow row={row} /> : null}
      {row.kind === "fork-context" ? <ForkContextTimelineRow row={row} /> : null}
      {row.kind === "proposed-plan" ? <ProposedPlanTimelineRow row={row} /> : null}
      {row.kind === "page" ? <PageTimelineRow row={row} /> : null}
      {row.kind === "command-request" ? (
        <div className="mx-auto max-w-4xl px-1">
          <CommandRequestRow request={row.request} />
        </div>
      ) : null}
      {row.kind === "subagent-result" ? <SubagentReceiptTimelineRow row={row} /> : null}
      {row.kind === "workflow-run" ? <WorkflowRunTimelineRow row={row} /> : null}
      {row.kind === "mcp-app" ? <McpAppTimelineRow row={row} /> : null}
      {row.kind === "working" ? <WorkingTimelineRow row={row} /> : null}
      {row.kind === "check-back" ? <CheckBackTimelineRow row={row} /> : null}
      {row.kind === "side-status" ? <SideStatusTimelineRow row={row} /> : null}
    </div>
  );
});

function MessageTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const ctx = use(TimelineRowCtx);
  switch (timelineMessageView(row.message, ctx.sideAnswerRequests)) {
    case "assistant":
      return <AssistantTimelineRow row={row} />;
    case "thread-message":
      return row.message.fromThread !== undefined ? (
        <ThreadMessageTimelineRow row={row} origin={row.message.fromThread} />
      ) : null;
    case "user":
      return <UserTimelineRow row={row} />;
    case "invite":
      return <InviteMessageTimelineRow row={row} />;
    case "side-answer-reply":
      return <SideAnswerReplyTimelineRow row={row} />;
    case "agent-message":
      return <AgentMessageTimelineRow row={row} />;
    case null:
      return null;
  }
}

function ForkContextTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "fork-context" }> }) {
  const ctx = use(TimelineRowCtx);
  const payload = row.forkContext.payload;
  const isNativeFork = row.forkContext.seedMode === "provider-native";
  const sourceRole = payload.sourceMessageRole === "assistant" ? "assistant" : "user";
  const contextCounts = [
    `${payload.includedMessageCount} message${payload.includedMessageCount === 1 ? "" : "s"}`,
    payload.includedToolSummaryCount > 0
      ? `${payload.includedToolSummaryCount} tool summar${
          payload.includedToolSummaryCount === 1 ? "y" : "ies"
        }`
      : null,
    payload.includedAttachmentCount > 0
      ? `${payload.includedAttachmentCount} image${payload.includedAttachmentCount === 1 ? "" : "s"}`
      : null,
  ].filter((part): part is string => part !== null);
  const attachmentNames = payload.attachments.map((attachment) => attachment.name).join(", ");

  return (
    <div className="mx-auto max-w-4xl px-1">
      <div className="surface-group px-3.5 py-3 text-sm">
        <div className="flex min-w-0 items-start gap-2.5">
          <SplitIcon className="mt-0.5 size-4 shrink-0 rotate-90 text-muted-foreground/70" />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <p className="font-medium text-foreground/90">Fork context</p>
              <p className="text-xs text-muted-foreground/60">
                {formatTimestamp(payload.createdAt, ctx.timestampFormat)}
              </p>
            </div>
            <p className="mt-1 text-xs text-muted-foreground/75">
              {isNativeFork
                ? "Current files were used. Full conversation history carried over (native provider fork)."
                : `Current files were used. Context carried over: ${contextCounts.join(", ") || "none"}.`}
            </p>
            <div className="mt-2 rounded-lg border border-group-divider px-3 py-2">
              <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/55">
                Source {sourceRole} message
              </p>
              <p className="line-clamp-3 text-xs text-muted-foreground/85">
                {payload.sourceMessageText || "No text in the source message."}
              </p>
            </div>
            {attachmentNames || payload.omittedAttachmentCount > 0 ? (
              <p className="mt-2 text-xs text-muted-foreground/70">
                {attachmentNames ? `Images: ${attachmentNames}.` : null}
                {payload.omittedAttachmentCount > 0
                  ? ` ${payload.omittedAttachmentCount} image${
                      payload.omittedAttachmentCount === 1 ? " was" : "s were"
                    } omitted.`
                  : null}
              </p>
            ) : null}
            {isNativeFork ? null : (
              <details className="mt-2 group/fork-context">
                <summary className="cursor-pointer select-none text-xs text-muted-foreground/75 transition-colors hover:text-foreground">
                  Carried context
                </summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-border/60 bg-background/55 p-3 text-[11px] leading-relaxed text-muted-foreground/85">
                  {payload.contextText}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const EMPTY_IMAGE_PREVIEW_ITEMS: ReadonlyArray<TimelineImagePreviewItem> = [];

/**
 * Message attachments carry HTTP preview URLs against the environment's base
 * URL. A saved environment's route is cross-origin and authenticated over its
 * WebSocket, which the browser cannot attach to an `<img>` request, so swap
 * those previews for data URLs fetched over the RPC channel. Locally-echoed
 * blob/data previews (composer handoff) pass through untouched. Only chat
 * attachments belong here: work entry images may carry foreign http URLs that
 * are not stored attachments.
 */
function useResolvedAttachmentPreviews(
  images: ReadonlyArray<TimelineImagePreviewItem>,
): ReadonlyArray<TimelineImagePreviewItem> {
  const ctx = use(TimelineRowCtx);
  const environmentId = ctx.activeThreadEnvironmentId;
  const rpcImages =
    images.length > 0 && environmentRequiresRpcAssetTransport(environmentId)
      ? images.filter((image) => image.previewUrl && /^https?:/i.test(image.previewUrl))
      : EMPTY_IMAGE_PREVIEW_ITEMS;
  const previewQueries = useQueries({
    queries: rpcImages.map((image) =>
      chatAttachmentPreviewQueryOptions({ environmentId, attachmentId: image.id }),
    ),
  });
  if (rpcImages.length === 0) {
    return images;
  }

  const dataUrlById = new Map<string, string | undefined>();
  rpcImages.forEach((image, index) => {
    dataUrlById.set(image.id, previewQueries[index]?.data);
  });
  return images.map((image) => {
    if (!dataUrlById.has(image.id)) {
      return image;
    }
    const dataUrl = dataUrlById.get(image.id);
    // While the RPC fetch is pending (or failed), drop the unreachable HTTP
    // URL so the grid shows its name placeholder instead of a broken image.
    return dataUrl ? { ...image, previewUrl: dataUrl } : { id: image.id, name: image.name };
  });
}

function TimelineFileAttachmentChips(props: {
  attachments: ReadonlyArray<ChatAttachment>;
  className?: string | undefined;
}) {
  const ctx = use(TimelineRowCtx);
  const files = props.attachments.filter((attachment) => attachment.type === "file");
  if (files.length === 0) {
    return null;
  }
  const environmentId = ctx.activeThreadEnvironmentId;
  return (
    <div className={cn("flex flex-wrap gap-1.5", props.className)}>
      {files.map((file) => (
        <button
          key={file.id}
          type="button"
          title={file.name}
          aria-label={`Preview ${file.name}`}
          className="inline-flex max-w-[260px] cursor-zoom-in items-center gap-1.5 rounded-md border border-border/80 bg-background/70 px-2 py-1 text-xs text-muted-foreground hover:text-foreground/80"
          onClick={() =>
            ctx.onPreviewFile({
              name: file.name,
              kind: file.kind,
              loadBlob: () => loadChatAttachmentBlob({ environmentId, attachmentId: file.id }),
            })
          }
        >
          <FileTextIcon className="size-3.5 shrink-0" />
          <span className="truncate">{file.name}</span>
        </button>
      ))}
    </div>
  );
}

function UserTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  if (isCommandOutputMessage(row.message.id)) {
    return <CommandOutputTimelineRow row={row} />;
  }
  return <UserMessageTimelineRow row={row} />;
}

/**
 * The output of a suggested command, sent to its agent as your message: one
 * line in your bubble, the full text a click away.
 */
function CommandOutputTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const ctx = use(TimelineRowCtx);
  const canRevertAgentWork = typeof row.revertTurnCount === "number";
  return (
    <div className="flex flex-col items-end">
      <div className="flex w-full justify-end">
        <div className="group relative max-w-[80%] min-w-0 rounded-4xl bg-secondary px-3.5 py-2.5">
          <CommandOutputMessage
            messageId={row.message.id}
            text={row.message.text}
            renderText={(text) => (
              <ChatMarkdown
                text={text}
                cwd={ctx.markdownCwd}
                environmentId={ctx.activeThreadEnvironmentId}
                threadId={ctx.activeThreadId ?? undefined}
              />
            )}
            footer={
              <div className="flex items-center justify-end gap-1.5">
                <div className="flex items-center gap-1.5 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100">
                  <MessageCopyButton text={row.message.text} />
                  {canRevertAgentWork && <RevertUserMessageButton messageId={row.message.id} />}
                </div>
                <p className="text-right text-xs tracking-tight tabular-nums text-muted-foreground/50">
                  {formatTimestamp(row.message.createdAt, ctx.timestampFormat)}
                </p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

function UserMessageTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const ctx = use(TimelineRowCtx);
  const messageAttachments = row.message.attachments ?? [];
  const userImages = useResolvedAttachmentPreviews(
    messageAttachments.filter((attachment) => attachment.type === "image"),
  );
  const displayedUserMessage = deriveDisplayedUserMessageState(row.message.text);
  const terminalContexts = displayedUserMessage.contexts;
  const transcriptHighlights = displayedUserMessage.transcriptHighlights;
  const pickedElements = displayedUserMessage.pickedElements.map((context, index) => ({
    // Stable enough to key a chip and name a reveal request: the blocks in a
    // sent message no longer move.
    id: `${row.message.id}:element:${index}`,
    createdAt: row.message.createdAt,
    context,
  }));
  const drawings = displayedUserMessage.drawings.map((entry, index) => ({
    id: `${row.message.id}:drawing:${index}`,
    entry,
  }));
  const canRevertAgentWork = typeof row.revertTurnCount === "number";
  const canRetryFailedTurn = ctx.failedTurnRetry?.messageId === row.message.id;

  const onTheSide = row.message.sideTurnId !== undefined;
  const addressee =
    ctx.roomAgents !== null && (row.message.participantId || onTheSide)
      ? messageAgentLabels(ctx.roomAgents, row.message, ctx.agentModelLabeler)?.get(
          roomAgentKey(row.message.participantId),
        )
      : undefined;

  return (
    <div className="flex flex-col items-end">
      {addressee ? (
        <div className="mb-1 pr-1 font-mono text-[10.5px] text-muted-foreground">
          to {addressee.name}
          {onTheSide ? " · on the side" : null}
        </div>
      ) : null}
      <div className="flex w-full justify-end">
        <div className="group relative max-w-[80%] rounded-4xl bg-secondary px-3.5 py-2.5">
          <TimelineFileAttachmentChips attachments={messageAttachments} className="mb-2" />
          <TimelineImagePreviewGrid
            images={userImages}
            className="mb-2 max-w-[420px]"
            imageClassName="max-h-[220px] object-cover"
          />
          <CollapsibleUserMessageBody
            text={displayedUserMessage.visibleText}
            terminalContexts={terminalContexts}
            transcriptHighlights={transcriptHighlights}
            pickedElements={pickedElements}
            drawings={drawings}
            transcriptMessage={{
              id: row.message.id,
              role: "user",
            }}
            skills={ctx.skills}
            forceExpanded={ctx.searchTargetMessageId === row.message.id}
            findOpenRequest={ctx.findOpenRequests.get(row.id) ?? 0}
            searchHighlightQuery={
              ctx.activeSearchTargetMessageId === row.message.id ? ctx.searchTargetQuery : undefined
            }
            footer={
              <>
                <div className="flex items-center gap-1.5">
                  {canRetryFailedTurn && <RetryUserMessageButton />}
                  <div className="flex items-center gap-1.5 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100">
                    {displayedUserMessage.copyText && (
                      <MessageCopyButton text={displayedUserMessage.copyText} />
                    )}
                    {displayedUserMessage.copyText && (
                      <ContinueInNewThreadButton messageId={row.message.id} />
                    )}
                    {canRevertAgentWork && <RevertUserMessageButton messageId={row.message.id} />}
                  </div>
                </div>
                <p className="text-right text-xs tracking-tight tabular-nums text-muted-foreground/50">
                  {formatTimestamp(row.message.createdAt, ctx.timestampFormat)}
                </p>
              </>
            }
          />
        </div>
      </div>
    </div>
  );
}

const NO_TERMINAL_CONTEXTS: ParsedTerminalContextEntry[] = [];
const NO_TRANSCRIPT_HIGHLIGHTS: ParsedTranscriptHighlightContextEntry[] = [];
const NO_PICKED_ELEMENTS: SentPickedElementEntry[] = [];
const NO_DRAWINGS: Array<{ id: string; entry: ParsedDrawingContextEntry }> = [];

/**
 * A message one room agent wrote to another through a room tool: a question,
 * an independent review, a hand-off or a hand-off's reply. Quieter than the
 * user's own messages, and never read as the user speaking.
 */
function AgentMessageTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const ctx = use(TimelineRowCtx);
  const { message } = row;
  const description = describeRoomAgentMessage({
    message,
    labels: messageAgentLabels(ctx.roomAgents, message, ctx.agentModelLabeler),
    openStatus: ctx.openAgentRequestByMessageId.get(message.id)?.status ?? null,
  });
  if (description === null) {
    return null;
  }
  const agentLine = row.agentLine;
  // How the request ended, under its text: indented with the text when it
  // is quoted. A side request's ending shows at the end of its block instead.
  const outcome = (quoted: boolean) =>
    description.outcomeNote !== null && message.sideTurnId === undefined ? (
      <p
        className={cn("mt-1 text-xs leading-4 text-muted-foreground/70", quoted && "pl-3")}
        data-room-request-outcome={message.requestOutcome}
      >
        {description.outcomeNote}
      </p>
    ) : null;
  // A hand-off's reply is the answer right above it: one line says where
  // it went instead of the same words again.
  if (agentLine?.echoesAnswer) {
    return (
      <div
        className="min-w-0 px-1"
        data-room-agent-message="reply-echo"
        title={formatTimestamp(message.createdAt, ctx.timestampFormat)}
      >
        <p className="flex min-w-0 items-center gap-1 text-xs leading-5 text-muted-foreground/70">
          <ReplyIcon aria-hidden className="size-3 shrink-0" />
          <span className="min-w-0 truncate">Sent to {description.to} as its reply</span>
        </p>
        {outcome(false)}
      </div>
    );
  }
  const body = (
    <CollapsibleUserMessageBody
      text={message.text}
      terminalContexts={NO_TERMINAL_CONTEXTS}
      transcriptHighlights={NO_TRANSCRIPT_HIGHLIGHTS}
      pickedElements={NO_PICKED_ELEMENTS}
      drawings={NO_DRAWINGS}
      skills={ctx.skills}
      // Spoken to another agent, a hand-off reads close to an answer; a
      // question asked on the side reads like the asker's notes.
      bodyClassName={
        agentLine?.placement === "single" ? "text-foreground/80" : "text-muted-foreground"
      }
      forceExpanded={ctx.searchTargetMessageId === message.id}
      findOpenRequest={ctx.findOpenRequests.get(row.id) ?? 0}
      searchHighlightQuery={
        ctx.activeSearchTargetMessageId === message.id ? ctx.searchTargetQuery : undefined
      }
    />
  );
  // In a room, a hand-off or a reply is its writer speaking: its logo and
  // name head it, like any stretch of that agent's.
  if (agentLine?.placement === "single") {
    return (
      <div
        className="group min-w-0 px-1 py-0.5"
        data-room-agent-message={message.requestKind ?? "message"}
        title={formatTimestamp(message.createdAt, ctx.timestampFormat)}
      >
        <AgentAuthorLine
          agent={agentLine.agent}
          turnId={agentLine.turnId}
          asWritten={agentLine.asWritten}
        >
          <span className="min-w-0 truncate text-muted-foreground">→ {description.to}</span>
          {description.kind ? (
            <>
              <span className="shrink-0 text-muted-foreground/50">·</span>
              <span className="shrink-0 text-muted-foreground">{description.kind}</span>
            </>
          ) : null}
        </AgentAuthorLine>
        {body}
        {outcome(false)}
      </div>
    );
  }
  // Asked from inside the asking agent's own stretch: its line says who asked.
  const fromOwnStretch =
    agentLine !== undefined && agentLine.agent === message.fromAgent?.participantId;
  return (
    <div
      className="group min-w-0 px-1 py-0.5"
      data-room-agent-message={message.requestKind ?? "message"}
      title={formatTimestamp(message.createdAt, ctx.timestampFormat)}
    >
      <div className="mb-1 flex min-h-5 min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 font-mono text-[10.5px] text-muted-foreground">
        <span className="min-w-0 truncate">
          {fromOwnStretch ? `to ${description.to}` : `${description.from} → ${description.to}`}
        </span>
        {description.kind ? (
          <>
            <span className="shrink-0 text-muted-foreground/50">·</span>
            <span className="shrink-0">{description.kind}</span>
          </>
        ) : null}
        {description.review ? (
          <>
            <span className="shrink-0 text-muted-foreground/50">·</span>
            <RoomReviewTag request={message} timestampFormat={ctx.timestampFormat} />
          </>
        ) : null}
        {message.text.length > 0 ? (
          <div className="ml-auto opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100">
            <MessageCopyButton
              text={message.text}
              ariaLabel="Copy message"
              size="icon-xs"
              variant="ghost"
              className="-my-1.5"
            />
          </div>
        ) : null}
      </div>
      {/* Inside the asker's stretch its line already sets the question off. */}
      {fromOwnStretch ? body : <div className="border-l border-border pl-3">{body}</div>}
      {outcome(!fromOwnStretch)}
    </div>
  );
}

/**
 * A message another thread's agent wrote (child threads): a request the
 * thread that started this one sent it, or the answer a thread this one
 * started sent back. Its author is that thread, named and one click away,
 * never the user.
 */
function ThreadMessageTimelineRow({
  row,
  origin,
}: {
  row: Extract<TimelineRow, { kind: "message" }>;
  origin: NonNullable<ChatMessage["fromThread"]>;
}) {
  const ctx = use(TimelineRowCtx);
  const { message } = row;
  const title = useThreadTitle(ctx.activeThreadEnvironmentId, origin.threadId);
  const isReport = origin.kind === "report";
  const author = title ?? (isReport ? "A thread it started" : "The thread that started it");
  return (
    <div
      className="group min-w-0 px-1 py-0.5"
      data-thread-message={origin.kind}
      title={formatTimestamp(message.createdAt, ctx.timestampFormat)}
    >
      <div className="mb-1 flex min-h-5 min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 font-mono text-[10.5px] text-muted-foreground">
        {ctx.onOpenThread ? (
          <button
            type="button"
            className="min-w-0 cursor-pointer truncate text-foreground/85 underline-offset-2 transition-colors hover:text-foreground hover:underline"
            data-thread-message-author={origin.threadId}
            onClick={() => ctx.onOpenThread?.(origin.threadId)}
          >
            {author}
          </button>
        ) : (
          <span className="min-w-0 truncate text-foreground/85">{author}</span>
        )}
        <span className="shrink-0 text-muted-foreground/50">·</span>
        <span className="shrink-0">{isReport ? "reply" : "request"}</span>
        {message.text.length > 0 ? (
          <div className="ml-auto opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100">
            <MessageCopyButton
              text={message.text}
              ariaLabel="Copy message"
              size="icon-xs"
              variant="ghost"
              className="-my-1.5"
            />
          </div>
        ) : null}
      </div>
      <div className="border-l border-border pl-3">
        <CollapsibleUserMessageBody
          text={message.text}
          terminalContexts={NO_TERMINAL_CONTEXTS}
          transcriptHighlights={NO_TRANSCRIPT_HIGHLIGHTS}
          pickedElements={NO_PICKED_ELEMENTS}
          drawings={NO_DRAWINGS}
          skills={ctx.skills}
          bodyClassName="text-foreground/80"
          forceExpanded={ctx.searchTargetMessageId === message.id}
          findOpenRequest={ctx.findOpenRequests.get(row.id) ?? 0}
          searchHighlightQuery={
            ctx.activeSearchTargetMessageId === message.id ? ctx.searchTargetQuery : undefined
          }
        />
      </div>
      {message.requestOutcome === "cancelled" ? (
        <p
          className="mt-1 pl-3 text-xs leading-4 text-muted-foreground/70"
          data-thread-message-outcome="cancelled"
        >
          {isReport ? "Not read: its delivery was cancelled first" : "Taken back before it ran"}
        </p>
      ) : null}
    </div>
  );
}

/**
 * An agent asked the user to bring in another agent for a review. The card
 * above the message box is where the user answers; this line records the
 * ask and how it went. What the reviewer was asked shows with its review.
 */
function InviteMessageTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const ctx = use(TimelineRowCtx);
  const { message } = row;
  const [showRequest, setShowRequest] = useState(false);
  const description = describeRoomInvite({
    message,
    labels: messageAgentLabels(ctx.roomAgents, message, ctx.agentModelLabeler),
    openStatus: ctx.openAgentRequestByMessageId.get(message.id)?.status ?? null,
  });
  return (
    <div
      className="min-w-0 px-1 py-0.5"
      data-room-agent-message="invite"
      title={formatTimestamp(message.createdAt, ctx.timestampFormat)}
    >
      <div className="mb-1 flex min-h-5 min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 font-mono text-[10.5px] text-muted-foreground">
        <span className="min-w-0 truncate">{`${description.from} → ${description.to}`}</span>
        <span className="shrink-0 text-muted-foreground/50">·</span>
        <span className="shrink-0" data-room-invite-status>
          {description.status}
        </span>
      </div>
      {message.invite !== undefined ? (
        <div className="border-l border-border pl-3">
          <p className="text-sm text-muted-foreground" data-chat-find-body="">
            {message.invite.reason}
          </p>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 font-mono text-[10.5px] text-muted-foreground/70">
            <span className="min-w-0 truncate">
              {message.invite.billing.label}
              {message.invite.billing.perUse ? " · billed per use" : ""}
            </span>
            <span className="shrink-0 text-muted-foreground/50">·</span>
            <button
              type="button"
              className="shrink-0 hover:text-foreground"
              aria-expanded={showRequest}
              onClick={() => setShowRequest((shown) => !shown)}
            >
              {showRequest ? "hide the request" : "the request"}
            </button>
          </div>
          {showRequest ? (
            <p className="mt-1 text-sm whitespace-pre-wrap break-words text-muted-foreground">
              {message.text}
            </p>
          ) : null}
        </div>
      ) : null}
      {description.outcomeNote !== null ? (
        <p
          className="mt-1 pl-3 text-xs leading-4 text-muted-foreground/70"
          data-room-request-outcome={message.requestOutcome}
        >
          {description.outcomeNote}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A side answer on its way to the agent that asked for it: an invited
 * agent's review, or an answer that outran its call. The answer itself is in
 * the chat already, so this is one line.
 */
function SideAnswerReplyTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const ctx = use(TimelineRowCtx);
  const { message } = row;
  const labels = messageAgentLabels(ctx.roomAgents, message, ctx.agentModelLabeler);
  const nameOf = (participantId: ThreadParticipantId | null | undefined) =>
    labels?.get(roomAgentKey(participantId))?.name ?? "an agent";
  const from = nameOf(message.fromAgent?.participantId);
  const to = nameOf(message.participantId);
  const request =
    message.requestId !== undefined ? ctx.sideAnswerRequests.get(message.requestId) : undefined;
  const what = request?.requestKind === "ask" ? "answer" : "review";
  return (
    <p
      className="min-w-0 truncate px-1 font-mono text-[10.5px] text-muted-foreground"
      data-room-agent-message="side-answer-reply"
      title={formatTimestamp(message.createdAt, ctx.timestampFormat)}
    >
      {message.requestOutcome === "cancelled"
        ? `${from}'s ${what} was not sent to ${to}: stopped first`
        : request?.requestOutcome === "failed"
          ? `Told ${to} that no ${what} came`
          : `Sent ${from}'s ${what} to ${to}`}
    </p>
  );
}

const TimelineImagePreviewGrid = memo(function TimelineImagePreviewGrid(props: {
  images: ReadonlyArray<TimelineImagePreviewItem>;
  className?: string | undefined;
  imageClassName?: string | undefined;
}) {
  if (props.images.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "grid gap-2",
        props.images.length === 1 ? "grid-cols-1" : "grid-cols-2",
        props.className,
      )}
    >
      {props.images.map((image) => (
        <TimelineImagePreviewTile
          key={image.id}
          image={image}
          images={props.images}
          imageClassName={props.imageClassName}
        />
      ))}
    </div>
  );
});

/**
 * One tile in the grid. A row that only named its image (a Codex `view_image`,
 * a Claude `Read` of a screenshot) loads the bytes here, on mount — the
 * timeline is virtualized, so only rows the reader has actually reached ask for
 * anything. Until they arrive, and forever if they never do, the tile shows the
 * file name exactly as it did before.
 */
function TimelineImagePreviewTile(props: {
  image: TimelineImagePreviewItem;
  images: ReadonlyArray<TimelineImagePreviewItem>;
  imageClassName?: string | undefined;
}) {
  const ctx = use(TimelineRowCtx);
  const loaded = useLocalImagePreview({
    environmentId: ctx.activeThreadEnvironmentId,
    cwd: ctx.markdownCwd,
    path: props.image.previewUrl ? undefined : props.image.path,
  });
  const previewUrl = props.image.previewUrl ?? loaded.dataUrl;

  return (
    <div className="overflow-hidden rounded-lg border border-border/80 bg-background/70">
      {previewUrl ? (
        <button
          type="button"
          className="h-full w-full cursor-zoom-in"
          aria-label={`Preview ${props.image.name}`}
          onClick={() => {
            const preview = buildExpandedImagePreview(
              props.images.map((candidate) =>
                candidate.id === props.image.id ? { ...candidate, previewUrl } : candidate,
              ),
              props.image.id,
            );
            if (!preview) return;
            ctx.onImageExpand(preview);
          }}
        >
          <img
            src={previewUrl}
            alt={props.image.name}
            className={cn("block h-auto w-full", props.imageClassName)}
          />
        </button>
      ) : (
        <div className="flex min-h-[72px] items-center justify-center px-2 py-3 text-center text-[11px] text-muted-foreground/70">
          {props.image.name}
        </div>
      )}
    </div>
  );
}

function RevertUserMessageButton({ messageId }: { messageId: MessageId }) {
  const ctx = use(TimelineRowCtx);
  const activity = use(TimelineRowActivityCtx);

  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      className="text-muted-foreground hover:text-foreground"
      disabled={activity.isRevertingCheckpoint || activity.isWorking}
      onClick={() => ctx.onRevertUserMessage(messageId)}
      aria-label="Revert to this message"
      tooltip="Revert to this message"
    >
      <Undo2Icon className="size-3" />
    </Button>
  );
}

function RetryUserMessageButton() {
  const ctx = use(TimelineRowCtx);
  const activity = use(TimelineRowActivityCtx);
  const retry = ctx.failedTurnRetry;
  if (!retry) {
    return null;
  }

  return (
    <Button
      type="button"
      size="xs"
      // Shown at rest under a failed message, so it is solid; the hover-only
      // actions beside it are plain.
      variant="outline"
      disabled={retry.isRetrying || activity.isWorking}
      onClick={retry.onRetry}
      aria-label="Retry this message"
      tooltip="Retry this message"
      className="gap-1 px-2 enabled:cursor-pointer"
    >
      <RefreshCwIcon className={cn("size-3", retry.isRetrying && "animate-spin")} />
      {retry.isRetrying ? "Retrying" : "Retry"}
    </Button>
  );
}

function ContinueInNewThreadButton({
  messageId,
  className,
}: {
  messageId: MessageId;
  className?: string;
}) {
  const ctx = use(TimelineRowCtx);
  const activity = use(TimelineRowActivityCtx);
  if (!ctx.onContinueInNewThread || activity.isWorking) {
    return null;
  }

  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      onClick={() => ctx.onContinueInNewThread?.(messageId)}
      aria-label="Branch from here"
      tooltip="Branch from here"
      className={cn(
        "text-muted-foreground hover:text-foreground enabled:cursor-pointer",
        className,
      )}
    >
      <SplitIcon className="size-3 rotate-90" />
    </Button>
  );
}

function titleCaseModelPart(value: string): string {
  return value.length > 0 ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
}

function formatFallbackModelName(modelId: string): string {
  const parts = modelId.trim().split(/[-_]+/u).filter(Boolean);
  if (parts.length === 0) {
    return modelId;
  }
  if (parts[0]?.toLowerCase() !== "claude") {
    return modelId;
  }

  const tailNumbers: string[] = [];
  while (parts.length > 1) {
    const tail = parts[parts.length - 1];
    if (!tail || !/^\d+$/u.test(tail)) {
      break;
    }
    tailNumbers.unshift(tail);
    parts.pop();
  }

  const family = parts
    .slice(1)
    .map((part) => titleCaseModelPart(part.toLowerCase()))
    .join(" ");
  const version =
    tailNumbers.length >= 2
      ? `${tailNumbers[tailNumbers.length - 2]}.${tailNumbers[tailNumbers.length - 1]}`
      : tailNumbers[0];
  return ["Claude", family, version].filter(Boolean).join(" ");
}

function FallbackAssistantResponseContainer({
  row,
  children,
}: {
  row: Extract<TimelineRow, { kind: "message" }>;
  children: ReactNode;
}) {
  const fallback = row.assistantModelFallback;
  if (!fallback) {
    return children;
  }

  const requestedModel = formatFallbackModelName(fallback.requestedModel);
  const activeModel = formatFallbackModelName(fallback.activeModel);

  return (
    <div
      data-assistant-fallback-response="true"
      className="max-w-full rounded-3xl border border-warning/30 bg-warning/6 px-3 py-2.5"
    >
      <div className="mb-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] leading-tight">
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning/12 px-2 py-0.5 font-medium text-warning-foreground">
          <CircleAlertIcon aria-hidden="true" className="size-3" />
          Fallback response
        </span>
        <span className="min-w-0 text-muted-foreground">
          Requested <span className="font-medium text-foreground/80">{requestedModel}</span>,
          answered by <span className="font-medium text-foreground/80">{activeModel}</span>.
        </span>
      </div>
      {children}
    </div>
  );
}

function AssistantTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const ctx = use(TimelineRowCtx);
  const messageText = row.message.text || (row.message.streaming ? "" : "(empty response)");
  const { isWorking } = use(TimelineRowActivityCtx);
  const streaming = Boolean(row.message.streaming);
  const revealRef = useRef<HTMLDivElement>(null);
  const { revealing } = useStreamingTextReveal(revealRef, streaming);
  // Still being written, or its last characters still landing. A main-turn
  // message left marked streaming by an agent that stopped mid-reply is not
  // being written; a side answer settles its own message when it ends.
  const writing = streaming && (isWorking || row.message.sideTurnId !== undefined);
  useStreamingHeightFloor(revealRef, writing || (!streaming && revealing));
  const authReconnect =
    ctx.providerAuthReconnect && isProviderAuthErrorChatMessage(row.message)
      ? ctx.providerAuthReconnect
      : null;
  // Notes carry the story while the agent works, on its line. A finished
  // turn's last message is its answer: the line bends into it, it reads at
  // full strength and carries the turn's footer, while the notes before it
  // fade to the steps' grey. A note's time stays one hover away.
  const summary = row.turnSummary;
  // A settled reply of a main turn: its shell code blocks get a Run button.
  const runScope = useMemo<CodeBlockRunScope | null>(
    () =>
      streaming || row.message.sideTurnId !== undefined || row.message.turnId == null
        ? null
        : {
            messageId: row.message.id,
            participantId: row.message.participantId ?? null,
            text: row.message.text,
          },
    [
      streaming,
      row.message.id,
      row.message.participantId,
      row.message.sideTurnId,
      row.message.turnId,
      row.message.text,
    ],
  );

  return (
    <>
      {/* Mid-turn responses settle in with the same fade the activity rows use,
          so a non-streamed message doesn't pop in fully formed. Settled rows
          skip it: it replays when virtualization remounts a row. */}
      <div
        ref={row.assistantTurnInProgress ? rowEntranceRef : undefined}
        className="min-w-0 px-1 py-0.5"
      >
        <div
          className={cn(
            "group/assistant-message block w-full max-w-full align-top [&_.chat-markdown]:transition-colors [&_.chat-markdown]:duration-300",
            row.settledNote && "[&_.chat-markdown]:text-muted-foreground/80",
            summary && "[&_.chat-markdown]:text-foreground",
          )}
          data-assistant-message-section="true"
          data-settled-note={row.settledNote ? "true" : undefined}
          title={summary ? undefined : formatTimestamp(row.message.createdAt, ctx.timestampFormat)}
        >
          {ctx.authorLineMessageIds.has(row.message.id) ? (
            <AgentAuthorLine
              agent={row.message.participantId ?? null}
              turnId={row.agentLine?.turnId ?? row.message.turnId ?? null}
              asWritten={
                row.agentLine?.asWritten ??
                row.message.agentModels?.[roomAgentKey(row.message.participantId)] ??
                null
              }
              onTheSide={row.message.sideTurnId !== undefined}
              review={row.sideReview}
              replyTo={row.sideReplyTo}
            />
          ) : null}
          {authReconnect ? (
            <ProviderAuthReconnectCard
              action={authReconnect}
              className="max-w-2xl"
              resolved={ctx.resolvedProviderAuthReconnectIds.has(row.id)}
              {...(ctx.onRunProviderAuthReconnect ? { onRun: ctx.onRunProviderAuthReconnect } : {})}
            />
          ) : (
            <FallbackAssistantResponseContainer row={row}>
              <div
                ref={revealRef}
                data-agent-response-body="true"
                data-assistant-message-body="true"
                data-transcript-message-body="true"
                data-transcript-message-id={row.message.id}
                data-transcript-message-role="assistant"
                data-transcript-message-streaming={row.message.streaming ? "true" : undefined}
                data-chat-find-body=""
              >
                <CodeBlockRunScopeContext value={runScope}>
                  <ChatMarkdown
                    text={messageText}
                    cwd={ctx.markdownCwd}
                    environmentId={ctx.activeThreadEnvironmentId}
                    threadId={ctx.activeThreadId ?? undefined}
                    isStreaming={Boolean(row.message.streaming)}
                    skills={ctx.skills}
                    searchHighlightQuery={
                      ctx.activeSearchTargetMessageId === row.message.id
                        ? ctx.searchTargetQuery
                        : undefined
                    }
                  />
                </CodeBlockRunScopeContext>
              </div>
            </FallbackAssistantResponseContainer>
          )}
          {/* The footer comes first so the turn's changes, which land a beat
              later, open below it instead of pushing it down. */}
          {summary ? <AssistantTurnFooter row={row} summary={summary} /> : null}
          <AssistantChangedFilesSection
            turnSummary={row.assistantTurnDiffSummary}
            isTurnInProgress={row.assistantTurnInProgress}
            routeThreadKey={ctx.routeThreadKey}
            resolvedTheme={ctx.resolvedTheme}
            onOpenTurnDiff={ctx.onOpenTurnDiff}
          />
        </div>
      </div>
    </>
  );
}

/**
 * A finished turn's footer under its answer: how long the agent worked, what
 * it changed, how its checks ended, the agents it ran, and when it answered.
 * It takes the working row's place at the tail, so a turn ending moves
 * nothing above it.
 */
function AssistantTurnFooter({
  row,
  summary,
}: {
  row: Extract<TimelineRow, { kind: "message" }>;
  summary: TurnSummary;
}) {
  const { onOpenAgentsPanel, timestampFormat } = use(TimelineRowCtx);
  const tracker = useTurnAgentTracker(summary.trackerTurnIds, summary.trackerAgentSpawnIds);
  const parts: Array<{ key: string; node: ReactNode }> = [];
  if (summary.workedMs !== null && summary.workedMs >= 1_000) {
    parts.push({
      key: "worked",
      node: <span className="shrink-0">Worked for {formatWorkingDuration(summary.workedMs)}</span>,
    });
  }
  if (summary.editedFileCount > 0) {
    parts.push({
      key: "edited",
      node: <span className="shrink-0">edited {pluralize(summary.editedFileCount, "file")}</span>,
    });
  }
  if (summary.checks) {
    const failed = summary.checks.failed > 0;
    parts.push({
      key: "checks",
      node: (
        <span
          className={cn(
            "shrink-0",
            failed ? "text-destructive-foreground/85" : "text-success-foreground/75",
          )}
          data-turn-footer-checks={failed ? "failed" : "passed"}
        >
          {failed ? `${pluralize(summary.checks.failed, "check")} failed` : "checks passed"}
        </span>
      ),
    });
  }
  if (tracker.summary && onOpenAgentsPanel) {
    parts.push({
      key: "agents",
      node: (
        <TurnAgentTrackerButton summary={tracker.summary} onOpen={() => onOpenAgentsPanel(null)} />
      ),
    });
  }
  parts.push({
    key: "time",
    node: (
      <span className="shrink-0 text-muted-foreground/40">
        {formatTimestamp(row.message.createdAt, timestampFormat)}
      </span>
    ),
  });

  return (
    <div
      className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs leading-5 text-muted-foreground/60 tabular-nums"
      data-turn-footer="true"
    >
      {parts.map((part, index) => (
        <Fragment key={part.key}>
          {index > 0 ? (
            <span aria-hidden="true" className="shrink-0 text-muted-foreground/35">
              ·
            </span>
          ) : null}
          {part.node}
        </Fragment>
      ))}
      <span className="ml-1 flex shrink-0 items-center gap-2">
        {row.message.text.trim().length > 0 ? (
          <ContinueInNewThreadButton
            messageId={row.message.id}
            className="pointer-events-none opacity-0 transition-opacity duration-200 group-hover/assistant-message:pointer-events-auto group-hover/assistant-message:opacity-100 group-focus-within/assistant-message:pointer-events-auto group-focus-within/assistant-message:opacity-100"
          />
        ) : null}
        <AssistantCopyButton row={row} />
      </span>
    </div>
  );
}

function AssistantCopyButton({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const assistantCopyState = resolveAssistantMessageCopyState({
    text: row.message.text ?? null,
    showCopyButton: row.showAssistantCopyButton,
    streaming: row.assistantCopyStreaming,
  });

  if (!assistantCopyState.visible) {
    return null;
  }

  return (
    <div className="flex items-center opacity-0 transition-opacity duration-200 group-hover/assistant-message:opacity-100 group-focus-within/assistant-message:opacity-100">
      <MessageCopyButton
        text={assistantCopyState.text ?? ""}
        size="icon-xs"
        className="text-muted-foreground hover:text-foreground"
      />
    </div>
  );
}

function ProposedPlanTimelineRow({
  row,
}: {
  row: Extract<TimelineRow, { kind: "proposed-plan" }>;
}) {
  const ctx = use(TimelineRowCtx);
  const planState = ctx.proposedPlanState;
  const proposedPlan = row.proposedPlan;
  const status: ProposedPlanCardStatus =
    proposedPlan.implementedAt !== null
      ? "implemented"
      : (proposedPlan.dismissedAt ?? null) !== null
        ? "dismissed"
        : planState === null || proposedPlan.id === planState.activePlanId
          ? "actionable"
          : "superseded";
  const implementationThreadId =
    status === "implemented" &&
    proposedPlan.implementationThreadId !== null &&
    proposedPlan.implementationThreadId !== planState?.activeThreadId
      ? proposedPlan.implementationThreadId
      : null;
  const isActionable = status === "actionable" && planState !== null;

  return (
    <div className="min-w-0 px-1 py-0.5">
      <ProposedPlanCard
        planMarkdown={proposedPlan.planMarkdown}
        findOpenRequest={ctx.findOpenRequests.get(row.id) ?? 0}
        environmentId={ctx.activeThreadEnvironmentId}
        threadId={ctx.activeThreadId ?? undefined}
        cwd={ctx.markdownCwd}
        workspaceRoot={ctx.workspaceRoot}
        status={status}
        onImplement={isActionable ? planState.onImplement : undefined}
        onImplementInNewThread={isActionable ? planState.onImplementInNewThread : undefined}
        onDismiss={isActionable ? planState.onDismiss : undefined}
        onOpenImplementationThread={
          implementationThreadId && planState
            ? () => planState.onOpenThread(implementationThreadId)
            : undefined
        }
      />
    </div>
  );
}

const COLLAPSED_MESSAGE_FADE_HEIGHT_REM = 1.75;
const COLLAPSED_MESSAGE_FADE_MASK = `linear-gradient(to bottom, black calc(100% - ${COLLAPSED_MESSAGE_FADE_HEIGHT_REM}rem), transparent)`;
const COLLAPSED_MESSAGE_FADE_STYLE: CSSProperties = {
  WebkitMaskImage: COLLAPSED_MESSAGE_FADE_MASK,
  maskImage: COLLAPSED_MESSAGE_FADE_MASK,
};

/**
 * A finished agent, as one line of the conversation. The full report is not
 * inlined: the rail's Agents tab owns the transcript, and this row is the
 * receipt that says it happened and takes you there.
 */
function SubagentReceiptTimelineRow({
  row,
}: {
  row: Extract<TimelineRow, { kind: "subagent-result" }>;
}) {
  const ctx = use(TimelineRowCtx);
  // Named as its live line named it, so the agent keeps one name as it lands.
  const displayName = formatSubagentListName(row.result);
  const summary = formatSubagentReceiptSummary(row.result.body);
  const agentThreadId = row.result.agentThreadId;
  const meta = [row.result.model, formatTimestamp(row.createdAt, ctx.timestampFormat)]
    .filter((part): part is string => typeof part === "string" && part.length > 0)
    .join(" · ");
  // A provider that serves no transcript for this agent has nothing to drill
  // into, so the receipt is just a line of the record.
  const onOpenAgentsPanel = ctx.onOpenAgentsPanel;
  const interactive = onOpenAgentsPanel !== null && agentThreadId.length > 0;

  const body = (
    <>
      {/* Leads with the same icon as the rail's Agents tab, so the receipt and
          the surface it opens read as one thing. */}
      <BotIcon aria-hidden="true" className="mt-1 size-3 shrink-0 text-muted-foreground/60" />
      <span className="min-w-0 flex-1 truncate text-[12px] leading-5">
        <span className="font-medium text-foreground/85">{displayName}</span>
        {summary ? (
          <>
            <span className="px-1.5 text-muted-foreground/30">·</span>
            <span className="text-muted-foreground/75">{summary}</span>
          </>
        ) : null}
      </span>
      <span className="mt-[3px] flex shrink-0 items-center gap-1 rounded border border-border/50 bg-background/60 px-1 py-px text-[9px] leading-none font-medium tracking-[0.08em] text-muted-foreground/70 uppercase">
        <CheckIcon aria-hidden="true" className="size-2.5" />
        Subagent finished
      </span>
      {meta ? (
        <span className="shrink-0 font-mono text-[10px] leading-5 text-muted-foreground/35 tabular-nums">
          {meta}
        </span>
      ) : null}
    </>
  );

  // A bordered clickable tile — the one shape the flat system reserves for
  // exactly this: a row that opens another surface.
  const rowClassName =
    "flex w-full min-w-0 items-start gap-2 rounded-md border border-border/50 px-2 py-1 text-left";

  return (
    // The receipt lands mid-turn the moment its agent finishes; the fade makes
    // that arrival read as an event rather than a row that was always there.
    <div ref={rowEntranceRef} className="min-w-0" data-subagent-receipt-row="true">
      {/* Heading its agent's stretch (a turn that delegated before it wrote),
          the receipt names the agent; px-1 lines the name up with the text. */}
      {row.agentLine?.heads ? (
        <div className="px-1">
          <AgentAuthorLine
            agent={row.agentLine.agent}
            turnId={row.agentLine.turnId}
            asWritten={row.agentLine.asWritten}
          />
        </div>
      ) : null}
      {interactive ? (
        <button
          type="button"
          className={cn(
            rowClassName,
            "group/subagent-receipt transition-colors hover:border-border/80 hover:bg-foreground/[0.03] focus-ring",
          )}
          aria-label={`Open ${displayName} transcript`}
          data-subagent-receipt-open="true"
          onClick={() => onOpenAgentsPanel(agentThreadId)}
        >
          {body}
          {/* The drill-in affordance: quiet at rest, named on hover. */}
          <ChevronRightIcon
            aria-hidden="true"
            className="mt-1 size-3 shrink-0 text-muted-foreground/30 transition-colors duration-150 group-hover/subagent-receipt:text-muted-foreground/70"
          />
        </button>
      ) : (
        <div className={rowClassName}>{body}</div>
      )}
    </div>
  );
}

/** An agent page; it carries its agent's name when its stretch starts there. */
function PageTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "page" }> }) {
  const ctx = use(TimelineRowCtx);
  if (ctx.activeThreadId === null) {
    return null;
  }
  return (
    <>
      {row.agentLine?.heads ? (
        <div className="px-1">
          <AgentAuthorLine
            agent={row.agentLine.agent}
            turnId={row.agentLine.turnId}
            asWritten={row.agentLine.asWritten}
          />
        </div>
      ) : null}
      <AgentPageRow
        environmentId={ctx.activeThreadEnvironmentId}
        threadId={ctx.activeThreadId}
        page={row.page}
        updated={row.updated}
      />
    </>
  );
}

function WorkflowRunTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "workflow-run" }> }) {
  const ctx = use(TimelineRowCtx);
  return (
    <div className="min-w-0 px-1">
      {row.agentLine?.heads ? (
        <AgentAuthorLine
          agent={row.agentLine.agent}
          turnId={row.agentLine.turnId}
          asWritten={row.agentLine.asWritten}
        />
      ) : null}
      <WorkflowRunCard run={row.run} actions={ctx.workflowRunActions} />
    </div>
  );
}

/** An MCP app under the step whose tool shipped it (McpAppCard). */
function McpAppTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "mcp-app" }> }) {
  const ctx = use(TimelineRowCtx);
  if (ctx.activeThreadId === null) {
    return null;
  }
  return (
    <div className="min-w-0 px-1">
      {row.agentLine?.heads ? (
        <AgentAuthorLine
          agent={row.agentLine.agent}
          turnId={row.agentLine.turnId}
          asWritten={row.agentLine.asWritten}
        />
      ) : null}
      <McpAppCard
        environmentId={ctx.activeThreadEnvironmentId}
        threadId={ctx.activeThreadId}
        app={row.app}
        onPutMessage={ctx.onPutMcpAppMessage}
      />
    </div>
  );
}

/** The turn's fixed anchor at the timeline tail: the surface's one live node,
 *  the turn timer, and the agent tracker live here from the first token to the
 *  last, whatever the rows above are doing — so nothing about "now" teleports
 *  mid-turn. Its word is the step running right now ("Reading service.ts"), or
 *  the state the turn is in ("Thinking", "Waiting for approval"). While the
 *  agent thinks out loud, its newest thought sits under the word. */
function WorkingTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "working" }> }) {
  const { onOpenAgentsPanel, activeThreadEnvironmentId, activeThreadId } = use(TimelineRowCtx);
  const turnAgents = use(TimelineTurnAgentsCtx);
  const commandOutputLine = useLiveCommandOutputLine(
    activeThreadEnvironmentId,
    activeThreadId,
    row.command,
  );
  const { isWorking } = use(TimelineRowActivityCtx);
  const liveSubagents = turnAgents?.subagents ?? [];
  const takenAgents = useTurnAgents(row.trackerTurnIds, row.trackerAgentSpawnIds);
  // Its count covers the turns whose trackers it took over, plus what the live
  // state holds: every agent of the running turn, or once the turn settled,
  // only the ones still working. A finished turn's settled agents keep their
  // count on its footer.
  const agentSummary = summarizeTurnAgents(
    mergeSubagentLists(
      isWorking
        ? liveSubagents
        : liveSubagents.filter((item) => isActiveSubagentStatus(item.status)),
      takenAgents,
    ),
  );
  const liveAgentRoster = formatLiveAgentStatusRows(liveSubagents);
  const liveAgentCount = liveSubagents.filter((item) => isActiveSubagentStatus(item.status)).length;
  const dotsState = workingDotsStateForLabel(row.label, liveAgentCount);
  const waitingOnUser = isWaitingOnUserState(dotsState);

  return (
    // The three dots are the anchor's whole "alive" signal. They sit in the
    // activity lines' icon column, so the word lines up with the steps above.
    // Amber means the agent stopped and needs the user. Even room above and
    // below keeps the word centered on the line's bend.
    <div className="py-1" data-turn-working-anchor="true">
      <div className="min-w-0 pl-1">
        {row.agentLine?.heads ? (
          <AgentAuthorLine
            agent={row.agentLine.agent}
            turnId={row.agentLine.turnId}
            asWritten={row.agentLine.asWritten}
          />
        ) : null}
        <p className="flex min-w-0 items-center gap-1.5 text-xs leading-4 text-muted-foreground/70">
          <span className="flex min-w-0 items-center gap-1 tabular-nums">
            <span
              className={cn(
                "flex min-w-0 items-center gap-1.5",
                waitingOnUser && "text-warning-foreground",
              )}
            >
              {/* -top-px: the 12px box centers half a pixel low against the
                  type's caps, and dots read best a hair above middle. */}
              <WorkingAnchorDots state={dotsState} className="relative -top-px -mr-0.5 shrink-0" />
              <span
                className="working-shimmer min-w-0 truncate"
                data-tone={waitingOnUser ? "warning" : undefined}
                data-turn-working-label="true"
              >
                {row.createdAt ? row.label : `${row.label}...`}
              </span>
            </span>
            {row.createdAt ? (
              <>
                <span className="shrink-0 text-muted-foreground/40">·</span>
                <span className="shrink-0">
                  <WorkingTimer createdAt={row.createdAt} />
                </span>
              </>
            ) : null}
          </span>
          {agentSummary && onOpenAgentsPanel ? (
            <>
              <span className="shrink-0 text-muted-foreground/35">·</span>
              <TurnAgentTrackerButton
                summary={agentSummary}
                onOpen={() => onOpenAgentsPanel(null)}
              />
            </>
          ) : null}
        </p>
        {/* What the agent is thinking, in its own summary's words, under the
            word it explains. Indented to the word, past the dots. One line,
            the newest sentence, so the row never grows while a thought streams;
            the whole thought is a hover away. */}
        {row.thought ? (
          <p
            ref={detailEntranceRef}
            className="truncate pl-[19px] text-[11px] leading-4 text-muted-foreground/55"
            title={row.thought}
            data-turn-working-thought="true"
          >
            {newestThoughtSentence(row.thought)}
          </p>
        ) : commandOutputLine ? (
          // The newest line the running command printed, in the same place.
          <p
            ref={detailEntranceRef}
            className="truncate pl-[19px] font-mono text-[11px] leading-4 text-muted-foreground/55"
            title={commandOutputLine}
            data-turn-working-output="true"
          >
            {commandOutputLine}
          </p>
        ) : null}
        {/* Each live agent keeps its own row, so concurrent updates change the
            action text in place instead of replacing another agent's status. */}
        <div className={agentSummary ? "min-h-4" : undefined}>
          <LiveAgentRoster roster={liveAgentRoster} />
        </div>
      </div>
    </div>
  );
}

/**
 * When the thread's agents will come back by themselves (checkBacks.ts): the
 * soonest, how long until then and why, with the rest on hover. Cancel stops
 * that agent's runtime, the only way to drop a check-back, so it ends every
 * check-back that agent set; one saved to the project outlives the runtime
 * and has no Cancel.
 */
function CheckBackTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "check-back" }> }) {
  const { roomAgents, onCancelCheckBack } = use(TimelineRowCtx);
  const nowMs = useRelativeTimeTick(30_000);
  const soonest = nextCheckBack(row.checkBacks);
  if (soonest === null) return null;
  const { label, when } = describeCheckBack(soonest, nowMs);
  // In a room, the line says which agent will come back.
  const agentName = roomAgents?.get(roomAgentKey(soonest.participantId))?.name ?? null;
  const others = row.checkBacks.filter((checkBack) => checkBack !== soonest);
  return (
    <div className="py-1" data-check-back-row="true">
      <p className="flex min-w-0 items-center gap-1.5 pl-1 text-xs leading-4 text-muted-foreground">
        <Clock3Icon aria-hidden className="size-3 shrink-0" />
        <span className="min-w-0 truncate" data-check-back-label="true">
          {agentName ? `${agentName}: ${label}` : label}
        </span>
        <span className="shrink-0 font-mono text-[11px] tabular-nums">· {when}</span>
        {others.length > 0 ? (
          <TooltipWrapper
            tooltip={
              <div className="flex flex-col gap-0.5">
                {others.map((checkBack) => (
                  <span key={`${checkBack.participantId ?? "own"}:${checkBack.id}`}>
                    {formatCheckBackTime(checkBack.scheduledFor, nowMs)}
                    {checkBack.description ? `: ${checkBack.description}` : ""}
                  </span>
                ))}
              </div>
            }
          >
            <span className="shrink-0 underline decoration-dotted underline-offset-[3px]">
              and {others.length} more
            </span>
          </TooltipWrapper>
        ) : null}
        {onCancelCheckBack && !soonest.durable ? (
          <TooltipWrapper tooltip="Stops this agent's session, which ends every check-back it set. It starts again when you send a message.">
            <button
              type="button"
              className="ms-1 shrink-0 rounded px-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              data-check-back-cancel="true"
              onClick={() => onCancelCheckBack(soonest)}
            >
              Cancel
            </button>
          </TooltipWrapper>
        ) : null}
      </p>
      {soonest.description ? (
        <p className="truncate pl-[19px] text-[11px] leading-4 text-muted-foreground">
          {soonest.description}
        </p>
      ) : null}
      {soonest.durable ? (
        <p className="truncate pl-[19px] text-[11px] leading-4 text-muted-foreground">
          Saved to this project, so it outlives this chat's session.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Where a side answer stands when it is not simply answered: still going
 * (with its own Stop, which leaves the working agent alone), stopping,
 * stopped, or failed.
 */
function SideStatusTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "side-status" }> }) {
  const {
    roomAgents,
    agentModelLabeler,
    openAgentRequestByMessageId,
    onStopSideAnswer,
    onToggleSideAnswer,
    onRevealMessage,
    timestampFormat,
  } = use(TimelineRowCtx);
  // The question names its answerer as it was asked.
  const labels = messageAgentLabels(roomAgents, row.question, agentModelLabeler);
  const name = labels?.get(roomAgentKey(row.participantId))?.name ?? "The agent";
  // The line under your side question sits under it, at the right: the
  // working agent's line runs on at the left.
  const underYourQuestion = row.question.fromAgent === undefined;
  if (row.state === "answered") {
    // Opened to watch it, the answer stayed under its question; folding it
    // posts it where it was done instead.
    return (
      <div
        className={cn("py-1", underYourQuestion && "flex justify-end")}
        data-side-answer-status={row.state}
      >
        <button
          type="button"
          aria-expanded
          className="inline-flex max-w-full cursor-pointer items-center gap-1 pl-1 text-xs leading-4 text-muted-foreground/70 transition-colors hover:text-foreground"
          onClick={() => onToggleSideAnswer(row.sideTurnId)}
        >
          <span className="min-w-0 truncate">
            {name} {row.review ? "reviewed" : "answered"}
          </span>
          <ChevronRightIcon aria-hidden className="size-3 shrink-0 rotate-90" />
        </button>
      </div>
    );
  }
  if (row.state === "answered-below") {
    // The working agent went on while this was answered: the answer was
    // posted where the chat had got to, and this line leads to it.
    const answerMessageId = row.answerMessageId;
    return (
      <div
        className={cn("py-1", underYourQuestion && "flex justify-end")}
        data-side-answer-status={row.state}
      >
        <button
          type="button"
          disabled={answerMessageId === null}
          className="inline-flex max-w-full cursor-pointer items-center gap-1 pl-1 text-xs leading-4 text-muted-foreground/70 transition-colors hover:text-foreground disabled:cursor-default"
          onClick={() => {
            if (answerMessageId !== null) onRevealMessage(answerMessageId);
          }}
        >
          <ArrowDownIcon aria-hidden className="size-3 shrink-0" />
          <span className="min-w-0 truncate">
            {name}&rsquo;s {row.review ? "review" : "answer"} is below
          </span>
        </button>
      </div>
    );
  }
  if (row.state === "answering" || row.state === "stopping") {
    const stopping = row.state === "stopping";
    // The step it is on, unless the working row already says it.
    const doing = stopping
      ? "stopping"
      : (row.liveStep ?? (row.review ? "reviewing independently" : "answering"));
    // Its caller stopped waiting: the answer goes to it as a message.
    const goesTo =
      !stopping && openAgentRequestByMessageId.get(row.question.id)?.replyAsMessage === true
        ? (labels?.get(roomAgentKey(row.question.fromAgent?.participantId))?.name ?? null)
        : null;
    return (
      <div className="py-1" data-side-answer-status={row.state}>
        <div
          className={cn(
            "flex min-w-0 items-center gap-x-1.5 gap-y-0.5 pl-1 text-xs leading-4 text-muted-foreground/70",
            // One line that names the step the answer is on: a long step
            // truncates rather than wrapping Stop onto a line of its own.
            row.compact ? "flex-nowrap" : "flex-wrap",
            underYourQuestion && "justify-end",
          )}
        >
          <WorkingAnchorDots state="working" className="relative -top-px -mr-0.5 shrink-0" />
          {row.compact ? (
            // The working agent goes on meanwhile: one line, which opens to
            // show the answer being written. It sits at the right, so as the
            // step changes the words grow to the left and Stop stays put.
            <button
              type="button"
              aria-expanded={row.expanded}
              className="inline-flex min-w-0 cursor-pointer items-center gap-1 transition-colors hover:text-foreground"
              onClick={() => onToggleSideAnswer(row.sideTurnId)}
            >
              <span className="min-w-0 truncate">
                {name} · {doing}
                {goesTo !== null ? ` · goes to ${goesTo} when done` : null}
              </span>
              <span className="shrink-0">
                · <WorkingTimer createdAt={row.question.createdAt} />
              </span>
              <ChevronRightIcon
                aria-hidden
                className={cn("size-3 shrink-0", row.expanded && "rotate-90")}
              />
            </button>
          ) : (
            <span className="min-w-0 truncate">
              {name} · {doing}
              {goesTo !== null ? ` · goes to ${goesTo} when done` : null}
            </span>
          )}
          {row.review ? (
            <>
              <span className="shrink-0 text-muted-foreground/35">·</span>
              <RoomReviewTag request={row.question} timestampFormat={timestampFormat} />
            </>
          ) : null}
          {!stopping && onStopSideAnswer ? (
            <>
              {row.compact ? null : <span className="shrink-0 text-muted-foreground/35">·</span>}
              <button
                type="button"
                className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => onStopSideAnswer(row.sideTurnId)}
              >
                Stop
              </button>
            </>
          ) : null}
        </div>
      </div>
    );
  }
  // An agent's request says why it ended (a timeout, the agent leaving); a
  // failure keeps the side answer's own error.
  const outcomeNote =
    row.question.requestOutcome === "failed"
      ? null
      : (describeRoomAgentMessage({
          message: row.question,
          labels,
          openStatus: null,
        })?.outcomeNote ?? null);
  return (
    <p
      className={cn(
        "pl-1 text-xs leading-4",
        row.state === "failed" && outcomeNote === null
          ? "text-destructive-foreground"
          : "text-muted-foreground/70",
      )}
      data-side-answer-status={row.state}
    >
      {outcomeNote ??
        (row.state === "failed"
          ? `${name} couldn't answer${row.error ? `: ${row.error}` : "."}`
          : `Stopped before ${name} finished.`)}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Self-ticking labels — update their own text nodes so elapsed-time display
// does not create a React commit every second while a response is streaming.
// ---------------------------------------------------------------------------

/** Live elapsed label for the active turn. */
function WorkingTimer({ createdAt }: { createdAt: string }) {
  return <TickingText active compute={() => formatWorkingTimerNow(createdAt)} />;
}

/**
 * One stretch of the agent's steps between two things it said: the looking
 * around folded into one grey line, the steps worth noticing on lines of their
 * own. The stretch the agent is still on shows its newest steps as they land
 * instead. Steps still running are left to the working row's live line. A turn's
 * agents ride on its answer's footer; a turn that ended without an answer shows
 * them on its first group instead.
 */
const WorkGroupSection = memo(function WorkGroupSection({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "work" }>;
}) {
  const {
    workspaceRoot,
    roomAgentNames,
    turnDiffSummaryByTurnId,
    onOpenAgentsPanel,
    anchorOwnsLiveAgents,
    sideAnswerContext,
    threadStartCallsByTurn,
  } = use(TimelineRowCtx);
  const { isWorking } = use(TimelineRowActivityCtx);
  const groupedEntries = useMemo(
    () =>
      coalesceFileChangeWorkEntries(
        row.groupedEntries,
        turnDiffSummaryByTurnId,
        workspaceRoot,
        // The stretch the agent is on shows its newest steps: a file it just
        // edited again is one of them.
        row.folded ? "first" : "latest",
      ),
    [row.folded, row.groupedEntries, turnDiffSummaryByTurnId, workspaceRoot],
  );
  // Where the agent started threads of its own: shown as its own record with
  // each thread's live status, outside the steps so a folded group never
  // hides them.
  const threadStartEntries = useMemo(
    () => groupedEntries.filter(isSettledThreadStartEntry),
    [groupedEntries],
  );
  // The ones that leave a record here; a call another call of its turn speaks
  // for leaves none.
  const recordedThreadStartEntries = useMemo(
    () =>
      threadStartEntries.filter((entry) => {
        const turnCalls = threadStartCallsByTurn.get(threadStartTurnKey(entry)) ?? [];
        const call = turnCalls.find((candidate) => candidate.id === entry.id);
        return call === undefined || threadStartCallHasRecord(call, turnCalls);
      }),
    [threadStartCallsByTurn, threadStartEntries],
  );
  const steps = useMemo(
    () =>
      groupedEntries.flatMap((entry) => {
        if (threadStartEntries.includes(entry)) return [];
        const step = activityStepFromWorkLogEntry(entry, {
          workspaceRoot,
          roomAgentNames,
          ...(isFileChangeWorkEntry(entry)
            ? { diff: summarizeWorkEntryDiffStat(entry, turnDiffSummaryByTurnId) }
            : {}),
        });
        return step ? [step] : [];
      }),
    [groupedEntries, roomAgentNames, threadStartEntries, turnDiffSummaryByTurnId, workspaceRoot],
  );
  const entriesById = useMemo(
    () => new Map(groupedEntries.map((entry) => [entry.id, entry] as const)),
    [groupedEntries],
  );
  const renderExtras = useCallback(
    (step: ActivityStep) => {
      const entry = entriesById.get(step.id);
      return entry && hasWorkEntryExtras(entry) ? <WorkEntryExtras entry={entry} /> : null;
    },
    [entriesById],
  );
  const turnAgentTracker = useTurnAgentTracker(row.trackerTurnIds, row.trackerAgentSpawnIds);
  // While the turn is live, the working row at the tail carries its agents.
  const showTracker =
    turnAgentTracker.summary !== null &&
    onOpenAgentsPanel !== null &&
    !(isWorking && row.inActiveExchange);
  const hasSettledSteps = steps.some((step) => !step.running);

  if (!hasSettledSteps && !showTracker && recordedThreadStartEntries.length === 0) {
    return null;
  }

  return (
    <div className="min-w-0 px-1 pt-0.5" data-work-group="true">
      {/* Heading its agent's stretch, the group names the agent. */}
      {row.agentLine?.heads ? (
        <AgentAuthorLine
          agent={row.agentLine.agent}
          turnId={row.agentLine.turnId}
          asWritten={row.agentLine.asWritten}
          onTheSide={row.sideTurnId !== undefined}
          review={
            row.sideTurnId !== undefined ? sideAnswerContext.get(row.sideTurnId)?.review : undefined
          }
          replyTo={
            row.sideTurnId !== undefined
              ? sideAnswerContext.get(row.sideTurnId)?.replyTo
              : undefined
          }
        />
      ) : null}
      {showTracker && turnAgentTracker.summary ? (
        <div
          className="flex min-w-0 items-center gap-[7px] text-xs leading-5 text-muted-foreground/60"
          data-turn-agents-line="true"
        >
          <BotIcon className="size-3 shrink-0 text-muted-foreground/45" aria-hidden="true" />
          <TurnAgentTrackerButton
            summary={turnAgentTracker.summary}
            onOpen={() => onOpenAgentsPanel?.(null)}
          />
        </div>
      ) : null}
      {showTracker && !anchorOwnsLiveAgents ? (
        <LiveAgentRoster roster={turnAgentTracker.liveRoster} />
      ) : null}
      {hasSettledSteps ? (
        <ActivityGroup
          steps={steps}
          renderExtras={renderExtras}
          folded={row.folded}
          live={!row.folded}
          durationMs={row.folded ? stretchDurationMs(row.groupedEntries) : null}
        />
      ) : null}
      {recordedThreadStartEntries.map((entry) => (
        <StartedThreadsRecord key={entry.id} entry={entry} />
      ))}
    </div>
  );
});

/**
 * Where the agent started threads of its own (child threads): one line for
 * the call, then each thread with its live status, a click from its chat. In
 * "Ask me first", the line also says how the user answered. When the calls'
 * results do not say which threads are whose, the turn's last call speaks
 * for all of them (describeThreadStartCall).
 */
function StartedThreadsRecord({ entry }: { entry: TimelineWorkEntry }) {
  const ctx = use(TimelineRowCtx);
  const turnId = entry.turnId ?? null;
  const startedInTurn = useChildThreadsStartedIn(
    ctx.activeThreadEnvironmentId,
    ctx.activeThreadId,
    turnId,
  );
  const requests = useChildRequestState(ctx.activeThreadEnvironmentId, ctx.activeThreadId);
  const record = useMemo(() => {
    const turnCalls = ctx.threadStartCallsByTurn.get(threadStartTurnKey(entry)) ?? [];
    const call = turnCalls.find((candidate) => candidate.id === entry.id) ?? {
      id: entry.id,
      turnId,
      result: parseThreadStartResult(entry.toolResult ?? entry.outputPreview),
    };
    return describeThreadStartCall({
      call,
      turnCalls: turnCalls.length > 0 ? turnCalls : [call],
      startedInTurn,
      openRequests: requests?.open ?? [],
    });
  }, [ctx.threadStartCallsByTurn, entry, requests, startedInTurn, turnId]);
  if (record === null) {
    return null;
  }
  const { heading, outcome, threads } = record;
  return (
    <div className="min-w-0" data-started-threads={entry.id}>
      <div className="flex min-w-0 items-center gap-[7px] text-xs leading-5 text-foreground/80">
        <GitForkIcon aria-hidden className="size-3 shrink-0 text-muted-foreground/60" />
        <span className="min-w-0 truncate" data-started-threads-heading>
          {heading}
        </span>
        {outcome !== null ? (
          <>
            <span className="shrink-0 text-muted-foreground/50">·</span>
            <span className="shrink-0 text-muted-foreground" data-started-threads-outcome>
              {outcome}
            </span>
          </>
        ) : null}
      </div>
      {threads.length > 0 ? (
        <ul className="ml-[19px]">
          {threads.map((thread) => (
            <StartedThreadLine key={thread.id} thread={thread} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** One thread the agent started: its live dot, title and status, opening its chat. */
function StartedThreadLine({ thread }: { thread: SidebarThreadSummary }) {
  const { onOpenThread } = use(TimelineRowCtx);
  const status = resolveThreadStatusPill({
    thread: { ...thread, ...(thread.lastSeenAt ? { lastVisitedAt: thread.lastSeenAt } : {}) },
  });
  const finished = status === null || status.label === "Completed";
  const resting =
    thread.latestTurn?.state === "completed"
      ? "finished"
      : thread.latestTurn?.state === "interrupted"
        ? "stopped"
        : undefined;
  return (
    <li>
      <button
        type="button"
        className="flex w-full min-w-0 cursor-pointer items-center gap-1.5 text-left text-xs leading-5 text-foreground/85 transition-colors hover:text-foreground disabled:cursor-default"
        disabled={onOpenThread === null}
        data-started-thread={thread.id}
        onClick={() => onOpenThread?.(thread.id)}
      >
        <span
          aria-label={status?.label ?? "Finished"}
          className={cn(
            "size-1.5 shrink-0 rounded-full",
            finished ? "bg-muted-foreground/45" : status.dotClass,
          )}
        />
        <span className="min-w-0 truncate">{thread.title}</span>
        <span className="ms-auto flex shrink-0 items-center gap-1.5">
          <ThreadStatusText
            thread={thread}
            status={status?.label === "Completed" ? null : status}
            resting={resting}
          />
          <ThreadProviderGlyph thread={thread} />
        </span>
      </button>
    </li>
  );
}

function hasWorkEntryExtras(entry: TimelineWorkEntry): boolean {
  return Boolean(entry.authReconnect || entry.mcpAuthReconnect || (entry.images?.length ?? 0) > 0);
}

/** What a step shows beyond its line: a sign-in card to act on, or the
 *  images it produced. */
function WorkEntryExtras({ entry }: { entry: TimelineWorkEntry }) {
  const {
    onRunProviderAuthReconnect,
    resolvedProviderAuthReconnectIds,
    mcpAuthReconnectStatusByServerName,
    onRunMcpAuthReconnect,
  } = use(TimelineRowCtx);
  const images = entry.images ?? [];
  return (
    <>
      {entry.authReconnect ? (
        <ProviderAuthReconnectCard
          action={entry.authReconnect}
          resolved={resolvedProviderAuthReconnectIds.has(entry.id)}
          {...(onRunProviderAuthReconnect ? { onRun: onRunProviderAuthReconnect } : {})}
        />
      ) : null}
      {entry.mcpAuthReconnect ? (
        <McpAuthReconnectCard
          action={entry.mcpAuthReconnect}
          status={mcpAuthReconnectStatusByServerName.get(entry.mcpAuthReconnect.serverName)}
          {...(onRunMcpAuthReconnect ? { onRun: onRunMcpAuthReconnect } : {})}
        />
      ) : null}
      {images.length > 0 ? (
        <TimelineImagePreviewGrid
          images={images}
          className="max-w-[420px]"
          imageClassName="max-h-[260px] object-contain"
        />
      ) : null}
    </>
  );
}

/** A flat, stable mini-roster for the live agents on this turn. Accent stays on
 *  the status dot; the neutral name and quieter action remain easy to separate.
 *  The rows share one grid, so the name column fits the longest name (often a
 *  task, formatSubagentListName) up to a cap and the steps line up beside it. */
function LiveAgentRoster({ roster }: { roster: LiveAgentStatusRoster }) {
  const { onOpenAgentsPanel } = use(TimelineRowCtx);
  if (roster.rows.length === 0 || !onOpenAgentsPanel) {
    return null;
  }

  return (
    <div
      ref={detailEntranceRef}
      className="ml-1 grid min-w-0 grid-cols-[0.375rem_fit-content(min(16rem,45%))_minmax(0,1fr)] gap-x-1.5"
      data-turn-live-agent-roster="true"
    >
      {roster.rows.map((row) => (
        <button
          key={row.id}
          type="button"
          className="group/live-agent col-span-full grid min-w-0 cursor-pointer grid-cols-subgrid items-center gap-x-1.5 text-left text-[11px] leading-4 outline-none focus-ring"
          data-turn-live-agent-status="true"
          data-turn-live-agent-state={row.status}
          title={`${row.name}: ${row.step}`}
          aria-label={`Open ${row.name}: ${row.step}`}
          onClick={() => onOpenAgentsPanel(row.agentThreadId)}
        >
          <span
            aria-hidden="true"
            className={cn(
              "size-1.5 rounded-full",
              row.status === "waiting" ? "bg-amber-500" : "bg-primary-graph",
            )}
          />
          <span
            className="truncate font-medium text-foreground/70 transition-colors duration-150 group-hover/live-agent:text-foreground/90"
            data-turn-live-agent-name="true"
          >
            {row.name}
          </span>
          <span
            className="truncate text-muted-foreground/55 transition-colors duration-150 group-hover/live-agent:text-muted-foreground/80"
            data-turn-live-agent-action="true"
          >
            {row.step}
          </span>
        </button>
      ))}
      {roster.hiddenCount > 0 ? (
        <button
          type="button"
          className="col-span-full ml-3 cursor-pointer text-left text-[10px] leading-4 text-muted-foreground/45 outline-none transition-colors duration-150 hover:text-muted-foreground/75 focus-ring"
          data-turn-live-agent-overflow="true"
          aria-label={`Open ${roster.hiddenCount.toLocaleString()} more active ${roster.hiddenCount === 1 ? "subagent" : "subagents"}`}
          onClick={() => onOpenAgentsPanel(null)}
        >
          +{roster.hiddenCount.toLocaleString()} more active
        </button>
      ) : null}
    </div>
  );
}

interface TurnAgentTracker {
  readonly summary: TurnAgentSummary | null;
  readonly liveRoster: LiveAgentStatusRoster;
}

/** The turn's agents, resolved from the turns this group owns the tracker for —
 *  which is only the turns that started in it, so a turn with several activity
 *  groups shows one tracker rather than the same one repeated down the turn.
 *  Derived once by the group so the receipt and the group's own render decision
 *  cannot disagree about whether there is a tracker to show. */
function useTurnAgentTracker(
  trackerTurnIds: ReadonlyArray<TurnId>,
  trackerAgentSpawnIds: ReadonlyArray<string> = [],
): TurnAgentTracker {
  const turnSubagents = useTurnAgents(trackerTurnIds, trackerAgentSpawnIds);
  return {
    // An empty selection summarizes to null on its own, so a turn that ran no
    // agents has no tracker whether or not the thread has agent state at all.
    summary: summarizeTurnAgents(turnSubagents),
    liveRoster: formatLiveAgentStatusRows(turnSubagents),
  };
}

/** The agents a tracker names: its turns' agents, or a turnless group's by
 *  spawn id, the live record first and the thread's history after it. */
function useTurnAgents(
  trackerTurnIds: ReadonlyArray<TurnId>,
  trackerAgentSpawnIds: ReadonlyArray<string> = [],
): ReadonlyArray<SubagentProgressItem> {
  const turnAgents = use(TimelineTurnAgentsCtx);
  const turnIds = useMemo(() => new Set(trackerTurnIds), [trackerTurnIds]);
  const spawnCallIds = useMemo(() => new Set(trackerAgentSpawnIds), [trackerAgentSpawnIds]);
  return useMemo(
    () =>
      turnAgents
        ? selectTurnAgents({
            live: turnAgents.subagents,
            history: turnAgents.history,
            turnIds,
            spawnCallIds,
          })
        : [],
    [turnAgents, turnIds, spawnCallIds],
  );
}

/** The clickable turn-agents chip: one status dot per agent plus the count,
 *  shared by the settled receipt and the live working row so the tracker looks
 *  the same wherever it renders. The dots are the live roster's, so the chip
 *  and the agents' live lines read as the same agents. */
function TurnAgentTrackerButton({
  summary,
  onOpen,
}: {
  summary: TurnAgentSummary;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className="inline-flex min-w-0 items-center gap-1.5 transition-colors duration-150 hover:text-foreground/75"
      aria-label={`${summary.text}. Open the agents panel.`}
      data-turn-agents-summary="true"
      onClick={onOpen}
    >
      <span aria-hidden="true" className="inline-flex shrink-0 items-center gap-[3px]">
        {summary.segments.map((segment) => (
          <span
            key={segment.id}
            className={cn(
              "block size-1.5 rounded-full",
              segment.status === "running" && "bg-primary-graph",
              segment.status === "waiting" && "bg-amber-500",
              segment.status === "failed" && "bg-destructive",
              segment.status === "stopped" && "bg-muted-foreground/35",
              segment.status === "completed" && "bg-muted-foreground/35",
            )}
          />
        ))}
      </span>
      <span className="truncate">{summary.text}</span>
    </button>
  );
}

/**
 * Edits to the same file within a stretch read as one step. `placement` says
 * where that step sits: where the file was first edited, or where it was
 * edited last. Either way it keeps the first edit's id.
 */
function coalesceFileChangeWorkEntries(
  entries: ReadonlyArray<TimelineWorkEntry>,
  turnDiffSummaryByTurnId: ReadonlyMap<TurnId, TurnDiffSummary>,
  workspaceRoot: string | undefined,
  placement: "first" | "latest",
): TimelineWorkEntry[] {
  const coalesced: Array<TimelineWorkEntry | null> = [];
  const indexByKey = new Map<string, number>();

  for (const entry of entries) {
    const enrichedEntry = withInferredFileChangePaths(entry, turnDiffSummaryByTurnId);
    const key = fileChangeCoalesceKey(enrichedEntry, turnDiffSummaryByTurnId, workspaceRoot);
    if (!key) {
      coalesced.push(enrichedEntry);
      continue;
    }

    const existingIndex = indexByKey.get(key);
    if (existingIndex === undefined) {
      indexByKey.set(key, coalesced.length);
      coalesced.push(enrichedEntry);
      continue;
    }

    const previous = coalesced[existingIndex];
    if (!previous) {
      coalesced.push(enrichedEntry);
      continue;
    }
    const merged = mergeFileChangeWorkEntries(previous, enrichedEntry, workspaceRoot);
    if (placement === "first") {
      coalesced[existingIndex] = merged;
    } else {
      coalesced[existingIndex] = null;
      indexByKey.set(key, coalesced.length);
      coalesced.push(merged);
    }
  }

  return coalesced.filter((entry) => entry !== null);
}

function withInferredFileChangePaths(
  entry: TimelineWorkEntry,
  turnDiffSummaryByTurnId: ReadonlyMap<TurnId, TurnDiffSummary>,
): TimelineWorkEntry {
  if (!isFileChangeWorkEntry(entry) || (entry.changedFiles?.length ?? 0) > 0) {
    return entry;
  }
  // A running edit that hasn't revealed its target (tool input still
  // streaming) must not adopt the turn's accumulated file list: that pins
  // the previous edits' files — and their whole-turn +/- totals — onto an
  // unrelated in-flight row.
  if (entry.executionState === "running") {
    return entry;
  }

  const turnSummary = resolveWorkEntryTurnDiffSummary(entry, turnDiffSummaryByTurnId);
  if (!turnSummary || turnSummary.files.length === 0) {
    return entry;
  }

  return {
    ...entry,
    changedFiles: dedupeChangedFilePaths(turnSummary.files.map((file) => file.path)),
  };
}

function fileChangeCoalesceKey(
  entry: TimelineWorkEntry,
  turnDiffSummaryByTurnId: ReadonlyMap<TurnId, TurnDiffSummary>,
  workspaceRoot: string | undefined,
): string | null {
  if (!isFileChangeWorkEntry(entry)) {
    return null;
  }
  if (entry.executionState === "running" || entry.executionState === "failed") {
    return null;
  }

  const changedFiles = dedupeChangedFilePaths(entry.changedFiles, workspaceRoot);
  if (changedFiles.length === 0) {
    return null;
  }

  const turnSummary = resolveWorkEntryTurnDiffSummary(entry, turnDiffSummaryByTurnId);
  const turnKey = entry.turnId ?? turnSummary?.turnId ?? "unkeyed";
  const pathKey = changedFiles
    .map((filePath) => normalizeFileChangeCoalescePath(filePath, workspaceRoot))
    .filter((filePath) => filePath.length > 0)
    .toSorted()
    .join("\u001e");
  if (!pathKey) {
    return null;
  }

  return ["file-change", turnKey, pathKey].join("\u001f");
}

function normalizeFileChangeCoalescePath(
  filePath: string,
  workspaceRoot: string | undefined,
): string {
  return normalizeDiffMatchPath(formatWorkspaceRelativePath(filePath, workspaceRoot));
}

function mergeFileChangeWorkEntries(
  previous: TimelineWorkEntry,
  next: TimelineWorkEntry,
  workspaceRoot: string | undefined,
): TimelineWorkEntry {
  const changedFiles = dedupeChangedFilePaths(
    [...(previous.changedFiles ?? []), ...(next.changedFiles ?? [])],
    workspaceRoot,
  );
  const changedFileStats = sumChangedFileStats(previous.changedFileStats, next.changedFileStats);
  const executionState = next.executionState ?? previous.executionState;
  return {
    ...previous,
    ...next,
    id: previous.id,
    createdAt: previous.createdAt,
    ...(changedFiles.length > 0 ? { changedFiles } : {}),
    ...(changedFileStats.length > 0 ? { changedFileStats } : {}),
    ...(executionState ? { executionState } : {}),
    ...(previous.turnId && !next.turnId ? { turnId: previous.turnId } : {}),
  };
}

/** Distinct edits coalesced into one row are separate diffs against the same
 *  file, so their +/- counts add up (unlike lifecycle updates of one call,
 *  which replace each other upstream). */
function sumChangedFileStats(
  previous: TimelineWorkEntry["changedFileStats"],
  next: TimelineWorkEntry["changedFileStats"],
): NonNullable<TimelineWorkEntry["changedFileStats"]>[number][] {
  const byPath = new Map<string, NonNullable<TimelineWorkEntry["changedFileStats"]>[number]>();
  for (const stat of previous ?? []) {
    byPath.set(normalizeDiffMatchPath(stat.path), stat);
  }
  for (const stat of next ?? []) {
    const key = normalizeDiffMatchPath(stat.path);
    const existing = byPath.get(key);
    byPath.set(
      key,
      existing
        ? {
            ...existing,
            additions: existing.additions + stat.additions,
            deletions: existing.deletions + stat.deletions,
          }
        : stat,
    );
  }
  return [...byPath.values()];
}

function isFileChangeWorkEntry(
  entry: Pick<TimelineWorkEntry, "changedFiles" | "itemType" | "requestKind">,
): boolean {
  return (
    entry.itemType === "file_change" ||
    entry.requestKind === "file-change" ||
    (entry.changedFiles?.length ?? 0) > 0
  );
}

function dedupeChangedFilePaths(
  paths: ReadonlyArray<string> | undefined,
  workspaceRoot?: string | undefined,
): string[] {
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const path of paths ?? []) {
    const trimmedPath = path.trim();
    const key =
      workspaceRoot === undefined
        ? normalizeDiffMatchPath(trimmedPath)
        : normalizeFileChangeCoalescePath(trimmedPath, workspaceRoot);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    deduped.push(trimmedPath);
  }
  return deduped;
}

/** Subscribes directly to the UI state store for expand/collapse state,
 *  so toggling re-renders only this component — not the entire list. */
const AssistantChangedFilesSection = memo(function AssistantChangedFilesSection({
  turnSummary,
  isTurnInProgress,
  routeThreadKey,
  resolvedTheme,
  onOpenTurnDiff,
}: {
  turnSummary: TurnDiffSummary | undefined;
  isTurnInProgress: boolean;
  routeThreadKey: string;
  resolvedTheme: "light" | "dark";
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
}) {
  if (!turnSummary) return null;
  if (isTurnInProgress) return null;
  const checkpointFiles = turnSummary.files;
  if (checkpointFiles.length === 0) return null;

  return (
    <AssistantChangedFilesSectionInner
      turnSummary={turnSummary}
      checkpointFiles={checkpointFiles}
      routeThreadKey={routeThreadKey}
      resolvedTheme={resolvedTheme}
      onOpenTurnDiff={onOpenTurnDiff}
    />
  );
});

/** Inner component that only mounts when there are actual changed files,
 *  so the store subscription is unconditional (no hooks after early return). */
function AssistantChangedFilesSectionInner({
  turnSummary,
  checkpointFiles,
  routeThreadKey,
  resolvedTheme,
  onOpenTurnDiff,
}: {
  turnSummary: TurnDiffSummary;
  checkpointFiles: TurnDiffSummary["files"];
  routeThreadKey: string;
  resolvedTheme: "light" | "dark";
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
}) {
  const defaultTreeExpanded = useSettings((settings) => settings.chatChangedFilesDefaultExpanded);
  const allDirectoriesExpanded = useUiStateStore(
    (store) =>
      store.threadChangedFilesExpandedById[routeThreadKey]?.[turnSummary.turnId] ??
      defaultTreeExpanded,
  );
  const setExpanded = useUiStateStore((store) => store.setThreadChangedFilesExpanded);
  const summaryStat = summarizeTurnDiffStats(checkpointFiles);
  const changedFileCountLabel = String(checkpointFiles.length);

  return (
    <div className="mt-2 surface-group p-2.5">
      {/* Wraps rather than overflowing. With the browser and source control
          both open the chat column gets narrow, and a row that cannot wrap
          pushes its buttons out of the card instead of under the label.
          It pins while a long tree scrolls under it, so it carries the
          group's fill as a solid color, and so does the strip above it. */}
      <div className="sticky top-2 z-10 mb-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 surface-group-solid before:absolute before:inset-x-0 before:-top-2 before:h-2 before:surface-group-solid before:content-['']">
        <p className="min-w-0 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/55">
          <span>Turn changes ({changedFileCountLabel})</span>
          {hasNonZeroStat(summaryStat) && (
            <>
              <span className="mx-1">/</span>
              <span className="font-mono font-normal tabular-nums tracking-normal">
                <DiffStatLabel
                  additions={summaryStat.additions}
                  deletions={summaryStat.deletions}
                />
              </span>
            </>
          )}
        </p>
        <div className="ms-auto flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            size="xs"
            variant="outline"
            data-scroll-anchor-ignore
            onClick={() =>
              setExpanded(
                routeThreadKey,
                turnSummary.turnId,
                !allDirectoriesExpanded,
                defaultTreeExpanded,
              )
            }
          >
            {allDirectoriesExpanded ? "Collapse tree" : "Expand tree"}
          </Button>
          <Button
            type="button"
            size="xs"
            variant="outline"
            data-scroll-anchor-ignore
            onClick={() => onOpenTurnDiff(turnSummary.turnId)}
          >
            View turn diff
          </Button>
        </div>
      </div>
      <ChangedFilesTree
        key={`changed-files-tree:${turnSummary.turnId}`}
        turnId={turnSummary.turnId}
        files={checkpointFiles}
        allDirectoriesExpanded={allDirectoriesExpanded}
        resolvedTheme={resolvedTheme}
        onOpenTurnDiff={onOpenTurnDiff}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leaf components
// ---------------------------------------------------------------------------

const UserMessageTerminalContextInlineLabel = memo(
  function UserMessageTerminalContextInlineLabel(props: { context: ParsedTerminalContextEntry }) {
    const tooltipText =
      props.context.body.length > 0
        ? `${props.context.header}\n${props.context.body}`
        : props.context.header;

    return <TerminalContextInlineChip label={props.context.header} tooltipText={tooltipText} />;
  },
);

const UserMessageTranscriptHighlightInlineLabel = memo(
  function UserMessageTranscriptHighlightInlineLabel(props: {
    context: ParsedTranscriptHighlightContextEntry;
  }) {
    const preview = formatTranscriptHighlightContextPreview(props.context);
    const roleWord = props.context.sourceRole === "assistant" ? "assistant" : "your";

    return (
      <Popover>
        <PopoverTrigger
          openOnHover
          delay={200}
          closeDelay={0}
          render={
            <button
              type="button"
              aria-label={`View note on highlighted ${roleWord} text`}
              className="inline-flex max-w-56 cursor-pointer items-center gap-1 rounded-md border border-border/70 bg-background/55 px-2 py-0.5 text-[11px] leading-5 text-muted-foreground/85 outline-none transition-colors hover:text-foreground focus-ring"
            >
              <SquarePenIcon className="size-3.5 shrink-0 opacity-70" />
              <span className="min-w-0 truncate">{`"${preview}"`}</span>
            </button>
          }
        />
        <PopoverPopup align="start" sideOffset={6} className="w-72 max-w-[calc(100vw-2rem)]">
          <TranscriptHighlightContextCard context={props.context}>
            <div className="flex flex-col gap-1.5">
              <span className={TRANSCRIPT_HIGHLIGHT_CARD_LABEL_CLASS_NAME}>Your note</span>
              <p className="max-h-32 overflow-y-auto whitespace-pre-wrap text-xs leading-snug text-foreground">
                {props.context.note}
              </p>
            </div>
          </TranscriptHighlightContextCard>
        </PopoverPopup>
      </Popover>
    );
  },
);

/** A picked element read back out of a sent message, keyed for rendering. */
interface SentPickedElementEntry {
  id: string;
  createdAt: string;
  context: PickedElementContext;
}

/**
 * The same chip the composer showed for this element, minus what no longer
 * applies: the message is sent, so the note reads as it was said and nothing
 * is removable. The way back to the element on the page stays.
 */
const UserMessagePickedElementChip = memo(function UserMessagePickedElementChip(props: {
  entry: SentPickedElementEntry;
}) {
  const ctx = use(TimelineRowCtx);
  const revealPickedElement = ctx.onRevealPickedElement;
  const threadId = ctx.activeThreadId;
  const { id, createdAt, context } = props.entry;
  const onReveal =
    revealPickedElement === undefined || threadId === null
      ? undefined
      : () => revealPickedElement({ ...context, id, threadId, createdAt });
  return <PickedElementContextChip context={context} chipId={id} onReveal={onReveal} />;
});

/** The group chip on a sent message: read-only, but every member's way back
 *  to the page stays clickable. */
const UserMessagePickedElementGroupChip = memo(function UserMessagePickedElementGroupChip(props: {
  entries: SentPickedElementEntry[];
}) {
  const ctx = use(TimelineRowCtx);
  const revealPickedElement = ctx.onRevealPickedElement;
  const threadId = ctx.activeThreadId;
  const first = props.entries[0];
  if (first === undefined) {
    return null;
  }
  const onRevealMember =
    revealPickedElement === undefined || threadId === null
      ? undefined
      : (index: number) => {
          const entry = props.entries[index];
          if (entry === undefined) {
            return;
          }
          revealPickedElement({
            ...entry.context,
            id: entry.id,
            threadId,
            createdAt: entry.createdAt,
          });
        };
  return (
    <PickedElementContextGroupChip
      contexts={props.entries.map((entry) => entry.context)}
      chipId={first.id}
      onRevealMember={onRevealMember}
    />
  );
});

const UserMessageDrawingInlineLabel = memo(function UserMessageDrawingInlineLabel(props: {
  entry: ParsedDrawingContextEntry;
}) {
  const descriptor = formatParsedDrawingDescriptor(props.entry);
  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={200}
        closeDelay={0}
        render={
          <button
            type="button"
            aria-label={`View ${descriptor}`}
            className="inline-flex max-w-56 cursor-pointer items-center gap-1.5 rounded-md border border-border/70 bg-accent/40 py-1 px-2 text-[12px] font-medium leading-tight text-foreground outline-none transition-colors hover:bg-accent/60 focus-visible:ring-1 focus-visible:ring-ring"
          >
            <PencilIcon className="size-3 shrink-0 text-muted-foreground/70" />
            <span className="min-w-0 truncate">{descriptor}</span>
            {props.entry.note === null ? null : (
              <span
                aria-label="Has a note"
                className="size-1 shrink-0 rounded-full bg-primary-readable"
              />
            )}
          </button>
        }
      />
      <PopoverPopup
        className="w-72"
        viewportClassName="p-2 [--viewport-inline-padding:--spacing(2)]"
        side="top"
        align="start"
      >
        <p className="truncate text-xs font-medium text-foreground">{descriptor}</p>
        {props.entry.url.length === 0 ? null : (
          <p className="mb-1.5 truncate font-mono text-[10px] text-muted-foreground/70">
            {props.entry.url}
          </p>
        )}
        {props.entry.circled.length === 0 ? null : (
          <ul className="mb-1.5 flex flex-col gap-0.5">
            {props.entry.circled.map((label) => (
              <li key={label} className="truncate font-mono text-[10px] text-muted-foreground">
                {label}
              </li>
            ))}
          </ul>
        )}
        {props.entry.note === null ? null : (
          <p className="max-h-32 overflow-y-auto whitespace-pre-wrap text-xs leading-snug text-foreground">
            {props.entry.note}
          </p>
        )}
      </PopoverPopup>
    </Popover>
  );
});

const CollapsibleUserMessageBody = memo(function CollapsibleUserMessageBody(props: {
  text: string;
  terminalContexts: ParsedTerminalContextEntry[];
  transcriptHighlights: ParsedTranscriptHighlightContextEntry[];
  pickedElements: SentPickedElementEntry[];
  drawings: Array<{ id: string; entry: ParsedDrawingContextEntry }>;
  transcriptMessage?: { id: MessageId; role: TranscriptHighlightSourceRole } | undefined;
  skills: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  forceExpanded?: boolean | undefined;
  /** Bumped each time find in chat opens it to show a match; it opens as a
   *  click would, and the reader can fold it again. */
  findOpenRequest?: number | undefined;
  searchHighlightQuery?: string | undefined;
  footer?: ReactNode;
  /** Overrides the text's classes (its color, for an agent's quieter message). */
  bodyClassName?: string | undefined;
}) {
  const findOpenRequest = props.findOpenRequest ?? 0;
  const [expanded, setExpanded] = useState(findOpenRequest > 0);
  const [seenFindOpenRequest, setSeenFindOpenRequest] = useState(findOpenRequest);
  if (findOpenRequest !== seenFindOpenRequest) {
    setSeenFindOpenRequest(findOpenRequest);
    if (findOpenRequest > 0) setExpanded(true);
  }
  const hasVisibleBody =
    props.text.trim().length > 0 ||
    props.terminalContexts.length > 0 ||
    props.transcriptHighlights.length > 0;
  const hasAttachedContextChips = props.pickedElements.length > 0 || props.drawings.length > 0;
  const canCollapse = hasVisibleBody && shouldCollapseUserMessage(props.text);
  const isCollapsed = canCollapse && !expanded && !props.forceExpanded;

  return (
    <div>
      {hasVisibleBody ? (
        <div
          className={cn("relative", isCollapsed && "max-h-44 overflow-hidden")}
          data-user-message-body="true"
          data-transcript-message-body={props.transcriptMessage ? "true" : undefined}
          data-transcript-message-id={props.transcriptMessage?.id}
          data-transcript-message-role={props.transcriptMessage?.role}
          data-user-message-collapsed={isCollapsed ? "true" : "false"}
          data-user-message-collapsible={canCollapse ? "true" : "false"}
          data-user-message-fade={isCollapsed ? "true" : "false"}
          data-chat-find-body=""
          data-chat-find-folded={isCollapsed ? "" : undefined}
          style={isCollapsed ? COLLAPSED_MESSAGE_FADE_STYLE : undefined}
        >
          <UserMessageBody
            text={props.text}
            terminalContexts={props.terminalContexts}
            transcriptHighlights={props.transcriptHighlights}
            skills={props.skills}
            searchHighlightQuery={props.searchHighlightQuery}
            className={props.bodyClassName}
          />
        </div>
      ) : null}
      {hasAttachedContextChips ? (
        // Outside the collapsible region: the chips are the evidence attached
        // to the message, and folding the text should not hide them.
        <div
          className={cn("flex flex-wrap gap-1.5", hasVisibleBody && "mt-2")}
          data-testid="sent-context-chips"
        >
          {/* Elements that were attached as one annotation come back out of
              the message sharing a groupId, and show as the one chip they
              were sent as. */}
          {groupPickedElementContexts(
            props.pickedElements.map((entry) => ({ ...entry.context, entry })),
          ).map((cluster) => {
            const first = cluster[0];
            if (first === undefined) {
              return null;
            }
            return cluster.length === 1 ? (
              <UserMessagePickedElementChip key={first.entry.id} entry={first.entry} />
            ) : (
              <UserMessagePickedElementGroupChip
                key={first.entry.id}
                entries={cluster.map((member) => member.entry)}
              />
            );
          })}
          {props.drawings.map(({ id, entry }) => (
            <UserMessageDrawingInlineLabel key={id} entry={entry} />
          ))}
        </div>
      ) : null}
      {canCollapse || props.footer ? (
        <div
          className={cn(
            "mt-1.5 flex items-center gap-2",
            canCollapse && props.footer ? "justify-between" : "justify-end",
          )}
          data-user-message-footer="true"
        >
          {canCollapse ? (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              aria-expanded={expanded}
              data-scroll-anchor-ignore
              onClick={() => setExpanded((value) => !value)}
              className="-ml-1 h-6 rounded-md px-1.5 text-xs text-muted-foreground/72 hover:bg-muted/55 hover:text-foreground/85"
            >
              {expanded ? "Show less" : "Show full message"}
            </Button>
          ) : null}
          {props.footer ? (
            <div className="ml-auto flex items-center gap-2">{props.footer}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});

const UserMessageBody = memo(function UserMessageBody(props: {
  text: string;
  terminalContexts: ParsedTerminalContextEntry[];
  transcriptHighlights: ParsedTranscriptHighlightContextEntry[];
  skills: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  searchHighlightQuery?: string | undefined;
  className?: string | undefined;
}) {
  const textClassName = cn(
    "whitespace-pre-wrap wrap-break-word text-sm leading-relaxed text-foreground",
    props.className,
  );
  // Quoted context the message carries, not the message's own words: find in
  // chat reads past it.
  const transcriptHighlightNodes =
    props.transcriptHighlights.length > 0 ? (
      <div className="mb-1.5 flex flex-wrap gap-1.5" data-chat-find-ignore>
        {props.transcriptHighlights.map((context) => (
          <UserMessageTranscriptHighlightInlineLabel
            key={`user-transcript-highlight:${context.sourceRole}:${context.sourceMessageId}:${context.selectedText}:${context.note}`}
            context={context}
          />
        ))}
      </div>
    ) : null;

  if (props.terminalContexts.length > 0) {
    const { embedded, pieces } = splitUserMessageText(props.text, props.terminalContexts);
    if (
      !embedded &&
      props.text.length === 0 &&
      buildInlineTerminalContextText(props.terminalContexts).length === 0
    ) {
      return null;
    }
    const inlineNodes = pieces.flatMap((piece): ReactNode[] =>
      piece.kind === "text"
        ? [
            <span key={`user-message-text:${piece.start}`}>
              <SkillInlineText
                text={piece.text}
                skills={props.skills}
                searchHighlightQuery={props.searchHighlightQuery}
              />
            </span>,
          ]
        : [
            <UserMessageTerminalContextInlineLabel
              key={`user-terminal-context-inline:${piece.context.header}`}
              context={piece.context}
            />,
            ...(embedded
              ? []
              : [
                  <span
                    key={`user-terminal-context-inline-space:${piece.context.header}`}
                    aria-hidden="true"
                  >
                    {" "}
                  </span>,
                ]),
          ],
    );
    return (
      <>
        {transcriptHighlightNodes}
        <div className={textClassName}>{inlineNodes}</div>
      </>
    );
  }

  if (props.text.length === 0 && transcriptHighlightNodes === null) {
    return null;
  }

  return (
    <>
      {transcriptHighlightNodes}
      {props.text.length > 0 ? (
        <div className={textClassName}>
          <SkillInlineText
            text={props.text}
            skills={props.skills}
            searchHighlightQuery={props.searchHighlightQuery}
          />
        </div>
      ) : null}
    </>
  );
});

// ---------------------------------------------------------------------------
// Structural sharing — reuse old row references when data hasn't changed
// so LegendList (and React) can skip re-rendering unchanged items.
// ---------------------------------------------------------------------------

/** Returns a structurally-shared copy of `rows`: for each row whose content
 *  hasn't changed since last call, the previous object reference is reused,
 *  and so is each part of what the rows share with every row. */
function useStableRows(rows: MessagesTimelineRow[]): StableMessagesTimelineRowsState {
  const prevState = useRef<StableMessagesTimelineRowsState>(NO_STABLE_TIMELINE_ROWS);

  return useMemo(() => {
    const nextState = computeStableMessagesTimelineRows(rows, prevState.current);
    prevState.current = nextState;
    return nextState;
  }, [rows]);
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** "45s", "3m 35s", "1h 12m". */
function formatWorkingDuration(elapsedMs: number): string {
  const elapsedSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  if (elapsedSeconds < 60) {
    return `${elapsedSeconds}s`;
  }

  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

function formatWorkingTimerNow(startIso: string): string {
  const startedAtMs = Date.parse(startIso);
  return Number.isFinite(startedAtMs) ? formatWorkingDuration(Date.now() - startedAtMs) : "0s";
}

function normalizeDiffMatchPath(filePath: string): string {
  return filePath
    .replaceAll("\\", "/")
    .replace(/^\/([A-Za-z]:\/)/, "$1")
    .replace(/^\.\/+/, "")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")
    .toLowerCase();
}

function diffPathsMatch(left: string, right: string): boolean {
  const normalizedLeft = normalizeDiffMatchPath(left);
  const normalizedRight = normalizeDiffMatchPath(right);
  if (!normalizedLeft || !normalizedRight) {
    return false;
  }
  return (
    normalizedLeft === normalizedRight ||
    normalizedLeft.endsWith(`/${normalizedRight}`) ||
    normalizedRight.endsWith(`/${normalizedLeft}`)
  );
}

function summarizeWorkEntryDiffStat(
  workEntry: Pick<TimelineWorkEntry, "changedFiles" | "changedFileStats" | "turnId">,
  turnDiffSummaryByTurnId: ReadonlyMap<TurnId, TurnDiffSummary>,
): { additions: number; deletions: number } | null {
  // Provider-reported stats are exact for the edits in this entry; the
  // checkpoint turn diff below is only a fallback (it can lag or be missing).
  const providerStats = workEntry.changedFileStats ?? [];
  if (providerStats.length > 0) {
    let additions = 0;
    let deletions = 0;
    for (const stat of providerStats) {
      additions += stat.additions;
      deletions += stat.deletions;
    }
    return { additions, deletions };
  }

  if ((workEntry.changedFiles?.length ?? 0) === 0) {
    return null;
  }
  const turnSummary = resolveWorkEntryTurnDiffSummary(workEntry, turnDiffSummaryByTurnId);
  if (!turnSummary) {
    return null;
  }

  const matchedDiffPaths = new Set<string>();
  let additions = 0;
  let deletions = 0;
  for (const diffFile of turnSummary.files) {
    if (
      !workEntry.changedFiles?.some((changedFile) => diffPathsMatch(changedFile, diffFile.path))
    ) {
      continue;
    }
    const matchKey = normalizeDiffMatchPath(diffFile.path);
    if (matchedDiffPaths.has(matchKey)) {
      continue;
    }
    matchedDiffPaths.add(matchKey);
    additions += diffFile.additions ?? 0;
    deletions += diffFile.deletions ?? 0;
  }

  return matchedDiffPaths.size > 0 ? { additions, deletions } : null;
}

function resolveWorkEntryTurnDiffSummary(
  workEntry: Pick<TimelineWorkEntry, "changedFiles" | "turnId">,
  turnDiffSummaryByTurnId: ReadonlyMap<TurnId, TurnDiffSummary>,
): TurnDiffSummary | null {
  if (workEntry.turnId) {
    return turnDiffSummaryByTurnId.get(workEntry.turnId) ?? null;
  }
  return null;
}

function providerAuthReconnectProviderLabel(provider: ProviderDriverKind): string {
  return PROVIDER_DISPLAY_NAMES[provider] ?? formatProviderDriverKindLabel(provider);
}

const ProviderAuthReconnectCard = memo(function ProviderAuthReconnectCard({
  action,
  onRun,
  className,
  resolved = false,
}: {
  action: ProviderAuthReconnectAction;
  onRun?: (action: ProviderAuthReconnectAction) => void;
  className?: string;
  resolved?: boolean;
}) {
  const providerLabel = providerAuthReconnectProviderLabel(action.provider);

  return (
    <div
      className={cn(
        "rounded-md border px-2.5 py-2",
        resolved ? "border-success/25 bg-success/5" : "border-destructive/25 bg-destructive/5",
        className,
      )}
      data-provider-auth-reconnect="true"
      data-provider-auth-reconnect-resolved={resolved ? "true" : "false"}
    >
      <div className="flex min-w-0 items-start gap-2">
        <span
          className={cn(
            "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-sm",
            resolved ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive",
          )}
        >
          {resolved ? (
            <CheckIcon className="size-3.5" aria-hidden />
          ) : (
            <LogInIcon className="size-3.5" aria-hidden />
          )}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-xs font-medium leading-5 text-foreground">
            {resolved ? `${providerLabel} sign-in refreshed` : `${providerLabel} needs sign in`}
          </p>
          <p className="text-[11px] leading-5 text-muted-foreground/80">
            {resolved ? (
              <>A later response succeeded. Retry the failed message if you still need it.</>
            ) : (
              <>
                Run <code className="font-mono text-foreground/85">{action.command}</code>, complete
                the browser sign-in, then retry this message.
              </>
            )}
          </p>
          <p className="line-clamp-2 text-[10px] leading-4 text-muted-foreground/55">
            Last error: {action.message}
          </p>
        </div>
        {resolved ? (
          <span className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-success/20 bg-success/5 px-2 text-xs font-medium text-success">
            <CheckIcon className="size-3" aria-hidden />
            Resolved
          </span>
        ) : (
          <Button
            type="button"
            size="xs"
            className="shrink-0"
            disabled={!onRun}
            onClick={(event) => {
              event.stopPropagation();
              onRun?.(action);
            }}
          >
            <TerminalIcon className="size-3" />
            Sign in in terminal
          </Button>
        )}
      </div>
    </div>
  );
});

const McpAuthReconnectCard = memo(function McpAuthReconnectCard({
  action,
  onRun,
  className,
  status,
}: {
  action: McpAuthReconnectAction;
  onRun?: (action: McpAuthReconnectAction) => void;
  className?: string;
  status?: McpAuthReconnectStatus | undefined;
}) {
  const resolved = status === "completed";
  const running = status === "running";

  return (
    <div
      className={cn(
        "rounded-md border px-2.5 py-2",
        resolved ? "border-success/25 bg-success/5" : "border-warning/25 bg-warning/5",
        className,
      )}
      data-mcp-auth-reconnect="true"
      data-mcp-auth-reconnect-status={status ?? "idle"}
    >
      <div className="flex min-w-0 items-start gap-2">
        <span
          className={cn(
            "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-sm",
            resolved ? "bg-success/10 text-success" : "bg-warning/10 text-warning",
          )}
        >
          {resolved ? (
            <CheckIcon className="size-3.5" aria-hidden />
          ) : running ? (
            <LoaderIcon className="size-3.5 animate-spin" aria-hidden />
          ) : (
            <KeyRoundIcon className="size-3.5" aria-hidden />
          )}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-xs font-medium leading-5 text-foreground">
            {resolved
              ? `${action.serverLabel} MCP authorized`
              : `${action.serverLabel} MCP needs login`}
          </p>
          <p className="text-[11px] leading-5 text-muted-foreground/80">
            {resolved
              ? "Authorization completed. Retry the failed message if this turn needed the MCP server."
              : "This MCP server did not start for this thread. Authorize it, then retry if this turn needed it."}
          </p>
          <p className="line-clamp-2 text-[10px] leading-4 text-muted-foreground/55">
            Last error: {action.message}
          </p>
        </div>
        {resolved ? (
          <span className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-success/20 bg-success/5 px-2 text-xs font-medium text-success">
            <CheckIcon className="size-3" aria-hidden />
            Authorized
          </span>
        ) : (
          <Button
            type="button"
            size="xs"
            className="shrink-0"
            disabled={!onRun || running}
            onClick={(event) => {
              event.stopPropagation();
              onRun?.(action);
            }}
          >
            {running ? (
              <LoaderIcon className="size-3 animate-spin" />
            ) : (
              <KeyRoundIcon className="size-3" />
            )}
            {running ? "Authorizing..." : action.actionLabel}
          </Button>
        )}
      </div>
    </div>
  );
});

/**
 * Who did this stretch of the chat: provider logo, name, and how hard its
 * model reasons. The logo sits in the lane to the left, where its line starts,
 * and the name lines up with the text below.
 */
function AgentAuthorLine({
  agent,
  turnId,
  asWritten,
  onTheSide = false,
  review,
  replyTo,
  children,
}: {
  /** A room agent, or null for the thread's own. */
  agent: ThreadParticipantId | null;
  /** The turn the stretch belongs to; null for the live one. */
  turnId: TurnId | null;
  /** How the agent was when it wrote here; null when nothing says. */
  asWritten: MessageAgentModel | null;
  /** More about the message after the name: who it went to, and why. */
  children?: ReactNode;
  /** A read-only answer given while another agent worked. */
  onTheSide?: boolean;
  /** An independent review's answer: the request, for its tag. */
  review?: ChatMessage | undefined;
  /** A side answer posted below the work that went on after its question:
   *  the question, to go back to. */
  replyTo?: ChatMessage | undefined;
}) {
  const { timestampFormat, onRevealMessage } = use(TimelineRowCtx);
  const label = use(TimelineAgentLabelCtx)(agent, turnId, asWritten);
  if (!label) {
    // The thread's own agent with no name to give (MessagesTimeline's
    // ownAgentLabels absent) goes unnamed.
    return agent === null ? null : (
      <div className="mb-1 font-mono text-[10.5px] text-muted-foreground">an agent that left</div>
    );
  }
  return (
    <div className="relative mb-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
      {label.entry ? (
        <ProviderInstanceIcon
          instanceId={label.entry.instanceId}
          driverKind={label.entry.driverKind}
          displayName={label.entry.displayName}
          accentColor={label.entry.accentColor}
          // An extra account wears its colored letter, so a reply says which
          // account wrote it even when two accounts run the same model.
          showBadge={providerInstanceShowsBadge(label.entry)}
          badgeClassName="right-[-0.3rem] bottom-[-0.2rem] h-2.5 min-w-2.5 px-0 text-[6px] shadow-none"
          // Centered on the line, 24px left of the text (the lane's 20px and
          // the section's 4px), 1px down to center on the name.
          className="absolute top-px -left-6 size-3.5"
          iconClassName="size-3.5"
        />
      ) : null}
      <span className="font-medium text-foreground">{label.name}</span>
      {label.reasoning ? (
        <>
          <span className="text-muted-foreground/50">·</span>
          <span className="text-muted-foreground" data-room-agent-reasoning>
            {label.reasoning}
          </span>
        </>
      ) : null}
      {children}
      {(review || onTheSide) && label.reasoning ? (
        <span className="text-muted-foreground/50">·</span>
      ) : null}
      {review ? (
        <RoomReviewTag request={review} timestampFormat={timestampFormat} />
      ) : onTheSide ? (
        <span className="text-muted-foreground">on the side</span>
      ) : null}
      {replyTo ? (
        <button
          type="button"
          className="inline-flex min-w-0 max-w-full cursor-pointer items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => onRevealMessage(replyTo.id)}
        >
          <ArrowUpIcon aria-hidden className="size-3 shrink-0" />
          <span className="min-w-0 truncate">
            {review ? "for the review asked above" : `answering “${questionSnippet(replyTo.text)}”`}
          </span>
        </button>
      ) : null}
    </div>
  );
}

/** A question's first words, for a link back to it. */
function questionSnippet(text: string): string {
  const line = text.trim().replace(/\s+/g, " ");
  return line.length > 48 ? `${line.slice(0, 47).trimEnd()}…` : line;
}
